<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDialog, type InputInst, type SelectGroupOption, type SelectOption } from 'naive-ui';
import { IconAdd, IconAlbums } from '@/components/design/icons';
import {
  GaiaEventType,
  PlaybookEvaluationType,
  PlaybookExtractionResponseType,
  ClauseValidationOptionType,
  PlaybookPartyPosition,
  PlaybookRiskLevel,
  PlaybookStatus,
  WebsocketSubscriptionTarget,
  type ClauseInDb,
  type ClauseSlim,
  type GaiaEvent,
  type ListPlaybookEvaluationsResponse,
  type PlaybookCreateRequest,
  type PlaybookEvaluationGroupInDb,
  type PlaybookInDb,
  type PlaybookTemplateSummary,
} from '@/api';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import WizardLayout from '@/components/design/layout/WizardLayout.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaLink from '@/components/design/base/GaiaLink.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaRiskPill from '@/components/design/base/GaiaRiskPill.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSectionHeader from '@/components/design/base/GaiaSectionHeader.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaEnumSelect from '@/components/design/base/selects/GaiaEnumSelect.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaChoiceCard from '@/components/design/base/GaiaChoiceCard.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaPickerOption from '@/components/design/composite/GaiaPickerOption.vue';
import GaiaRemovableList, {
  type GaiaRemovableItem,
} from '@/components/design/composite/GaiaRemovableList.vue';
import GaiaStepFlow from '@/components/design/composite/GaiaStepFlow.vue';
import { type GaiaStepRailItem } from '@/components/design/composite/GaiaStepRail.vue';
import GaiaSubPanel from '@/components/design/composite/GaiaSubPanel.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import GaiaWizardFooter from '@/components/design/composite/GaiaWizardFooter.vue';
import GaiaClauseTierChip from '@/components/design/playbook/GaiaClauseTierChip.vue';
import GaiaOutcomeCard from '@/components/design/playbook/GaiaOutcomeCard.vue';
import GaiaPlaybookItem from '@/components/design/playbook/GaiaPlaybookItem.vue';
import GaiaTriggerRow from '@/components/design/playbook/GaiaTriggerRow.vue';
import {
  clauseTierMap,
  contractTypeNames,
  playbookRiskLevelMap,
} from '@/components/design/statusMaps';
import GaiaClauseFormModal from '@/components/design/domain/clauses/GaiaClauseFormModal.vue';
import ClausePickerModal from '@/components/clauses/ClausePickerModal.vue';
import PlaybookActionModal from '@/components/playbooks/PlaybookActionModal.vue';
import UncommonsTriggerModal from '@/components/playbooks/UncommonsTriggerModal.vue';
import GroupTriggerModal from '@/components/playbooks/GroupTriggerModal.vue';
import VaultFileBrowser from '@/components/file_vault/VaultFileBrowser.vue';
import GaiaVaultUploadPanel from '@/components/design/domain/fileVault/GaiaVaultUploadPanel.vue';
import type { VaultUploadPanelApi } from '@/components/design/domain/fileVault/vaultUploadPanel';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import {
  actionTypeIcon,
  clauseOptionTypeLabel,
  evaluationTypeLabel,
  findingThresholdTriggers,
  formatFindingThresholdRule,
  formatGroupTriggerRule,
  extractionTypeOptions,
  extractionTypeLabel,
  extractionResponseType,
  type PlaybookFindingThresholdTrigger,
} from '@/services/playbooks/playbookRunDisplay';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import type { LibraryClause } from '@/services/clauseLibraryService';
import { usePlaybooksStore } from '@/stores/playbooksStore';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { playbookEditRoute } from '@/router/links';

type StepKey = 'setup' | 'clauses' | 'evaluations' | 'uncommons' | 'extraction' | 'groups';
type SourceMode = 'upload' | 'vault';
type CreationMethod = 'ai' | 'template' | 'scratch';
type ClauseModalMode = 'create' | 'edit';
type PlaybookEvaluation = ListPlaybookEvaluationsResponse[number];
type PlaybookEvaluationOption = NonNullable<PlaybookEvaluation['options']>[number];
type PlaybookOptionAction = NonNullable<PlaybookEvaluationOption['actions']>[number];
const CREATE_GROUP_KEY = '__create_group__';
type ClassificationOptionDraft = {
  key: string;
  uid: string | null;
  name: string;
  definition: string;
  color: string | null;
  riskLevel: PlaybookRiskLevel | null;
};

const playbookOptionTypes: ClauseValidationOptionType[] = [
  ClauseValidationOptionType.PREFERRED,
  ClauseValidationOptionType.FALLBACK,
  ClauseValidationOptionType.ESCALATION,
];

interface StepDefinition {
  key: StepKey;
  /** Rail label, and the target named by "Continue to …". */
  title: string;
  /** Line under the rail label, shown only while the step is the active one. */
  description: string;
  /** Heading above the step body. */
  heading: string;
  /** Sentence under the heading; doubles as the empty state's body. */
  subtitle: string;
  /** Primary action on an item step. Setup has none — its action is the footer's. */
  addLabel?: string;
}

const steps: StepDefinition[] = [
  {
    key: 'setup',
    title: 'Setup',
    description: 'Name the playbook and choose how its content is created.',
    heading: 'Set up your playbook',
    subtitle:
      'Define the playbook details, then choose how its clauses and evaluations are created.',
  },
  {
    key: 'extraction',
    title: 'Extractions',
    description: 'Information pulled out of every document.',
    heading: 'Extractions',
    subtitle: 'Facts Insights reads out of each document — dates, values, terms.',
    addLabel: 'Add extraction',
  },
  {
    key: 'evaluations',
    title: 'Evaluations',
    description: 'Questions asked about the document.',
    heading: 'Evaluations',
    subtitle: 'Questions answered for each document you review with this playbook.',
    addLabel: 'Add evaluation',
  },
  {
    key: 'uncommons',
    title: 'Uncommons',
    description: 'Passages that look unusual or out of place.',
    heading: 'Uncommons',
    subtitle: 'Patterns that should be flagged when they show up in a document.',
    addLabel: 'Add uncommon',
  },
  {
    key: 'clauses',
    title: 'Clauses',
    description: 'The standard your documents are checked against.',
    heading: 'Clauses',
    subtitle: 'Your positions per clause — the standard each document is compared to.',
    addLabel: 'Add clause',
  },
  {
    key: 'groups',
    title: 'Groups',
    description: 'Evaluation groups and their assigned evaluations.',
    heading: 'Groups',
    subtitle: 'Bundle evaluations so results read as a structured review, not a flat list.',
    addLabel: 'Add group',
  },
];

interface CreationMethodOption {
  value: CreationMethod;
  title: string;
  description: string;
}

// The three mutually exclusive ways a new playbook's initial content can be created.
const creationMethods: CreationMethodOption[] = [
  {
    value: 'ai',
    title: 'Generate with AI',
    description:
      'Generate clauses and evaluations using a reference contract, instructions, or both.',
  },
  {
    value: 'template',
    title: 'Use a template',
    description: 'Create the playbook from one of our predefined templates.',
  },
  {
    value: 'scratch',
    title: 'Start from scratch',
    description: 'Create an empty playbook and add clauses and evaluations manually.',
  },
];

const router = useRouter();
const route = useRoute();
const message = useGaiaToast();
const dialog = useDialog();
const store = usePlaybooksStore();
const contractTypeStore = useContractTypeStore();

/**
 * Confirmation for the destructive row actions. The wizard's item rows come from
 * GaiaPlaybookItem / GaiaRemovableList, whose remove buttons live inside the
 * component — there is no trigger element left to hang an n-popconfirm on.
 */
function confirmRemove(title: string, onConfirm: () => void): void {
  dialog.warning({
    title,
    positiveText: 'Delete',
    negativeText: 'Cancel',
    onPositiveClick: onConfirm,
  });
}
const props = defineProps<{
  step: StepKey;
  edit?: boolean;
}>();

const activeStep = computed<StepKey>(() => props.step);
const isEdit = computed<boolean>(() => props.edit || route.query.edit === 'true');
const fieldsDisabled = computed<boolean>(() => submitting.value || loadingPlaybook.value);
// The creation-method panel only matters while creating a brand-new playbook. Once the playbook
// exists (or we're editing), the content is already created, so we hide the panel instead of showing
// a disabled create form when the user navigates back to setup.
const showCreationPanel = computed<boolean>(() => !isEdit.value && !createdPlaybook.value);
const sourceMode = ref<SourceMode>('upload');
const creationMethod = ref<CreationMethod>('ai');
// The summary prompt is folded away behind a link, as in the mock; applyPlaybook
// springs it open when the playbook being edited already has one.
const showSummaryPrompt = ref<boolean>(false);
// The name's inline error only makes sense once the field has been visited.
const nameTouched = ref<boolean>(false);
const referenceUploadPanel = ref<VaultUploadPanelApi | null>(null);
const referenceUploadCount = ref<number>(0);
const selectedVaultKeys = ref<string[]>([]);
const templates = ref<PlaybookTemplateSummary[]>([]);
const templatesLoading = ref<boolean>(false);
const templatesLoaded = ref<boolean>(false);
const templatesError = ref<string | null>(null);
const selectedTemplateKey = ref<string | null>(null);
const templateSearch = ref<string>('');
const submitting = ref<boolean>(false);
const error = ref<string | null>(null);
const createdPlaybook = ref<PlaybookInDb | null>(null);
const loadingPlaybook = ref<boolean>(false);
const evaluations = ref<PlaybookEvaluation[]>([]);
const evaluationsLoading = ref<boolean>(false);
const evaluationsError = ref<string | null>(null);
const evaluationGroups = ref<PlaybookEvaluationGroupInDb[]>([]);
const showCreateGroupModal = ref<boolean>(false);
const groupTargetEvaluation = ref<PlaybookEvaluation | null>(null);
const newGroupName = ref<string>('');
const newGroupNameInput = ref<InputInst | null>(null);
const creatingGroup = ref<boolean>(false);
const showEditGroupModal = ref<boolean>(false);
const editGroupTarget = ref<PlaybookEvaluationGroupInDb | null>(null);
const editGroupName = ref<string>('');
const editGroupNameInput = ref<InputInst | null>(null);
const savingEditGroup = ref<boolean>(false);
const groupDeleting = reactive<Record<string, boolean>>({});
const showGroupTriggerModal = ref<boolean>(false);
const groupTriggerTarget = ref<PlaybookEvaluationGroupInDb | null>(null);
const playbookUpdatedReceived = ref<boolean>(false);
const optionDrafts = reactive<Record<string, string>>({});
const optionSaving = reactive<Record<string, boolean>>({});
const evaluationDeleting = reactive<Record<string, boolean>>({});
const collapsedEvaluations = reactive<Record<string, boolean>>({});
const activeEvaluationUid = ref<string | null>(null);
const activeGroupUid = ref<string | null>(null);
const showClauseEditModal = ref<boolean>(false);
const clauseEditMode = ref<ClauseModalMode>('edit');
const editingSourceClause = ref<ClauseSlim | null>(null);
const showClausePickerModal = ref<boolean>(false);
const addingClauseEvaluation = ref<boolean>(false);
const showClassificationOptionsModal = ref<boolean>(false);
const classificationModalMode = ref<ClauseModalMode>('edit');
const classificationOptionsModalEvaluation = ref<PlaybookEvaluation | null>(null);
const classificationOptionsModalName = ref<string>('');
const classificationOptionsModalPrompt = ref<string>('');
const CHOICE_TYPE_OPTIONS = [
  { label: 'Single choice', value: 'single' },
  { label: 'Multiple choice', value: 'multiple' },
];

const classificationOptionsModalMultiple = ref<boolean>(false);
const classificationOptionsModalRows = ref<ClassificationOptionDraft[]>([]);
const classificationOptionsRemovedUids = ref<string[]>([]);
const classificationOptionsModalSaving = ref<boolean>(false);
const showUncommonsModal = ref<boolean>(false);
const uncommonsModalMode = ref<ClauseModalMode>('create');
const uncommonsModalEvaluation = ref<PlaybookEvaluation | null>(null);
const uncommonsModalName = ref<string>('');
const uncommonsModalPrompt = ref<string>('');
const uncommonsModalSaving = ref<boolean>(false);
const showExtractionModal = ref<boolean>(false);
const extractionModalMode = ref<ClauseModalMode>('create');
const extractionModalEvaluation = ref<PlaybookEvaluation | null>(null);
const extractionModalName = ref<string>('');
const extractionModalPrompt = ref<string>('');
const extractionModalResponseType = ref<PlaybookExtractionResponseType>(
  PlaybookExtractionResponseType.STR
);
const extractionModalSaving = ref<boolean>(false);
const showActionModal = ref<boolean>(false);
const actionModalMode = ref<ClauseModalMode>('create');
const actionModalTriggerUid = ref<string | null>(null);
const actionModalEvaluationUid = ref<string | null>(null);
const actionModalOptionUid = ref<string | null>(null);
const actionModalAction = ref<PlaybookOptionAction | null>(null);
const actionModalSaving = ref<boolean>(false);
type ActionTriggerScope = 'evaluation' | 'group';
const actionModalTriggerScope = ref<ActionTriggerScope>('evaluation');
const actionDeleting = reactive<Record<string, boolean>>({});
const showTriggerModal = ref<boolean>(false);
const triggerModalMode = ref<ClauseModalMode>('create');
const triggerModalEvaluationUid = ref<string | null>(null);
const triggerModalTrigger = ref<PlaybookFindingThresholdTrigger | null>(null);
const triggerModalSaving = ref<boolean>(false);
const triggerDeleting = reactive<Record<string, boolean>>({});
let newClassificationOptionIndex = 0;

const optionColorSwatches = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#fbbf24',
  '#facc15',
  '#eab308',
  '#a3e635',
  '#84cc16',
  '#22c55e',
  '#16a34a',
];
let unsubscribePlaybookUpdates: (() => void) | null = null;

const form = reactive<PlaybookCreateRequest>({
  name: '',
  contract_type_uids: [],
  party_position: PlaybookPartyPosition.COMPANY,
  status: PlaybookStatus.DRAFT,
  language: 'English',
  description: null,
  summary_prompt: null,
  add_clauses_to_library: false,
  add_actions: false,
});

// Free-text generation prompt for AI generation. Assembled into `user_input.generation_prompt`
// at submit time rather than living on the form (which is typed as PlaybookCreateRequest).
const generationPrompt = ref<string>('');

const isAiCreation = computed<boolean>(() => creationMethod.value === 'ai');
const isTemplateCreation = computed<boolean>(() => creationMethod.value === 'template');
const isScratchCreation = computed<boolean>(() => creationMethod.value === 'scratch');

const partyPositionOptions: SelectOption[] = [
  { label: 'Company', value: PlaybookPartyPosition.COMPANY },
  { label: 'Third party', value: PlaybookPartyPosition.THIRD_PARTY },
  { label: 'Neutral', value: PlaybookPartyPosition.NEUTRAL },
];

const statusOptions: SelectOption[] = [
  { label: 'Draft', value: PlaybookStatus.DRAFT },
  { label: 'Active', value: PlaybookStatus.ACTIVE },
  { label: 'Archived', value: PlaybookStatus.ARCHIVED },
];

const selectedVaultFileUid = computed<string | null>(() => {
  const key = selectedVaultKeys.value.find(value => value.startsWith('file-'));
  return key ? key.replace(/^file-/, '') : null;
});

const selectedVaultFileLabel = computed<string>(() => {
  if (!selectedVaultFileUid.value) return 'No vault file selected';
  return 'Vault file selected';
});

// Templates grouped by their template group, narrowed by the search box. Groups left with no
// matches are dropped so the list doesn't show empty headings.
const templateGroups = computed<{ label: string; templates: PlaybookTemplateSummary[] }[]>(() => {
  const query = templateSearch.value.trim().toLowerCase();
  const matches = query
    ? templates.value.filter(template =>
        [template.name, template.description ?? '', template.group].some(field =>
          field.toLowerCase().includes(query)
        )
      )
    : templates.value;

  const groups = new Map<string, PlaybookTemplateSummary[]>();
  for (const template of matches) {
    const members = groups.get(template.group) ?? [];
    members.push(template);
    groups.set(template.group, members);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, members]) => ({ label, templates: members }));
});

function templateCountsLabel(template: PlaybookTemplateSummary): string {
  const evaluations = `${template.evaluation_count} ${template.evaluation_count === 1 ? 'evaluation' : 'evaluations'}`;
  const actions = `${template.action_count} ${template.action_count === 1 ? 'action' : 'actions'}`;
  return `${evaluations} · ${actions}`;
}

async function fetchPlaybookTemplates(): Promise<void> {
  templatesLoading.value = true;
  templatesError.value = null;
  try {
    templates.value = await PlaybooksService.listTemplates();
    templatesLoaded.value = true;
  } catch (error_) {
    templatesError.value = extractErrorDetail(error_, 'Failed to load playbook templates.');
  } finally {
    templatesLoading.value = false;
  }
}

// The templates list is only needed once the template method is picked, so it loads lazily.
watch(creationMethod, method => {
  if (method === 'template' && !templatesLoaded.value && !templatesLoading.value) {
    void fetchPlaybookTemplates();
  }
});

// Mirror the selected template's metadata into the details form so the user sees what the
// created playbook will look like. The template endpoint itself ignores the form.
watch(selectedTemplateKey, key => {
  const template = templates.value.find(t => t.key === key);
  if (!template) return;
  form.name = template.name;
  form.party_position = template.party_position;
  form.language = template.language;
  // Templates name their types by template key; the org's copies carry that key.
  void contractTypeStore.ensureLoaded().then(() => {
    form.contract_type_uids = template.contract_type_keys.flatMap(
      templateKey => contractTypeStore.byKey(templateKey)?.uid ?? []
    );
  });
});

const routedPlaybookUid = computed<string | null>(() => {
  const uid = route.params.uid;
  return typeof uid === 'string' && uid.length > 0 ? uid : null;
});

// A reference source is present when a file is attached (upload mode) or a vault file is
// selected (vault mode).
const hasReferenceSource = computed<boolean>(() =>
  sourceMode.value === 'vault'
    ? Boolean(selectedVaultFileUid.value)
    : referenceUploadCount.value > 0
);

// AI generation needs something to work from: a reference contract, generation instructions,
// or both. Either one on its own is enough.
const hasAiInput = computed<boolean>(
  () => hasReferenceSource.value || generationPrompt.value.trim().length > 0
);

const setupComplete = computed<boolean>(() => {
  if (createdPlaybook.value) return true;

  // A selected template fully defines the playbook, so the form fields don't apply.
  if (isTemplateCreation.value) return Boolean(selectedTemplateKey.value);

  const metadataComplete =
    form.name.trim().length > 0 && form.language.trim().length > 0 && Boolean(form.party_position);
  if (!metadataComplete) return false;

  // Starting from scratch needs metadata only.
  return isAiCreation.value ? hasAiInput.value : true;
});

// Explains why "Create playbook" is unavailable, since the button itself is disabled.
const setupValidationMessage = computed<string | null>(() => {
  if (createdPlaybook.value || isScratchCreation.value) return null;
  if (isTemplateCreation.value) {
    return selectedTemplateKey.value ? null : 'Select a template to create this playbook.';
  }
  return hasAiInput.value
    ? null
    : 'Provide a reference contract or generation instructions to generate the playbook.';
});

const currentStepIndex = computed(() => steps.findIndex(step => step.key === activeStep.value));

const nextStep = computed<StepDefinition | null>(() =>
  currentStepIndex.value >= 0 && currentStepIndex.value < steps.length - 1
    ? steps[currentStepIndex.value + 1]
    : null
);

const canGoBack = computed<boolean>(() => currentStepIndex.value > 0);
const hasEvaluations = computed<boolean>(() => evaluations.value.length > 0);
const clauseValidationEvaluations = computed<PlaybookEvaluation[]>(() =>
  evaluations.value.filter(evaluation => evaluation.evaluation_type === 'clause_validation')
);
const importedClauseUids = computed<string[]>(() =>
  clauseValidationEvaluations.value
    .map(evaluation => evaluationLinkedClause(evaluation)?.uid)
    .filter((uid): uid is string => Boolean(uid))
);
const classificationEvaluations = computed<PlaybookEvaluation[]>(() =>
  evaluations.value.filter(evaluation => evaluation.evaluation_type === 'classification')
);
// Classification evaluations without options fail at run time, so they block the step.
const classificationEvaluationsMissingOptions = computed<PlaybookEvaluation[]>(() =>
  classificationEvaluations.value.filter(evaluation => (evaluation.options ?? []).length === 0)
);
const uncommonsCheckEvaluations = computed<PlaybookEvaluation[]>(() =>
  evaluations.value.filter(evaluation => evaluation.evaluation_type === 'uncommons_check')
);
const extractionEvaluations = computed<PlaybookEvaluation[]>(() =>
  evaluations.value.filter(evaluation => evaluation.evaluation_type === 'extraction')
);
const displayedEvaluations = computed<PlaybookEvaluation[]>(() => {
  switch (activeStep.value) {
    case 'evaluations':
      return classificationEvaluations.value;
    case 'uncommons':
      return uncommonsCheckEvaluations.value;
    case 'extraction':
      return extractionEvaluations.value;
    default:
      return clauseValidationEvaluations.value;
  }
});
const hasDisplayedEvaluations = computed<boolean>(() => displayedEvaluations.value.length > 0);
const groupsByEvaluationUid = computed<Map<string, PlaybookEvaluationGroupInDb[]>>(() => {
  const map = new Map<string, PlaybookEvaluationGroupInDb[]>();
  for (const group of evaluationGroups.value) {
    for (const evaluationUid of group.evaluation_uids ?? []) {
      const existing = map.get(evaluationUid);
      if (existing) {
        existing.push(group);
      } else {
        map.set(evaluationUid, [group]);
      }
    }
  }
  return map;
});

function groupsForEvaluation(evaluation: PlaybookEvaluation): PlaybookEvaluationGroupInDb[] {
  return groupsByEvaluationUid.value.get(evaluation.uid) ?? [];
}

function groupDisplayName(group: PlaybookEvaluationGroupInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return group.name?.trim() || 'Untitled group';
}

const evaluationsByUid = computed<Map<string, PlaybookEvaluation>>(
  () => new Map(evaluations.value.map(evaluation => [evaluation.uid, evaluation]))
);

function evaluationsForGroup(group: PlaybookEvaluationGroupInDb): PlaybookEvaluation[] {
  return (group.evaluation_uids ?? [])
    .map(uid => evaluationsByUid.value.get(uid))
    .filter((evaluation): evaluation is PlaybookEvaluation => Boolean(evaluation));
}

function groupTriggerForActions(group: PlaybookEvaluationGroupInDb): string | null {
  return group.triggers?.[0]?.uid ?? null;
}

function groupActionEntries(
  group: PlaybookEvaluationGroupInDb
): { triggerUid: string; action: PlaybookOptionAction }[] {
  return (group.triggers ?? []).flatMap(trigger =>
    (trigger.actions ?? []).map(action => ({
      triggerUid: trigger.uid,
      action,
    }))
  );
}

// A group trigger is a single group-level threshold ({ mode, min_risk_level, min_count }).
function groupTriggerSummaries(group: PlaybookEvaluationGroupInDb): string[] {
  return (group.triggers ?? []).map(formatGroupTriggerRule);
}

// Routes to the same edit entry point the evaluation card header uses, by type.
function openEditEvaluation(evaluation: PlaybookEvaluation): void {
  if (isUncommonsEvaluation(evaluation)) {
    openEditUncommonsEvaluation(evaluation);
  } else if (isExtractionEvaluation(evaluation)) {
    openEditExtractionEvaluation(evaluation);
  } else if (isClauseValidationEvaluation(evaluation)) {
    openSourceClauseEdit(evaluation);
  } else {
    openClassificationOptionsModal(evaluation);
  }
}

function buildGroupOptions(evaluation: PlaybookEvaluation): SelectOption[] {
  const memberUids = new Set(groupsForEvaluation(evaluation).map(group => group.uid));
  const options: SelectOption[] = evaluationGroups.value.map(group => ({
    label: groupDisplayName(group),
    value: group.uid,
    disabled: memberUids.has(group.uid),
  }));
  options.push({ label: 'Create new group…', value: CREATE_GROUP_KEY });
  return options;
}

function removeEvaluationFromGroupUid(evaluation: PlaybookEvaluation, groupUid: string): void {
  const group = evaluationGroups.value.find(candidate => candidate.uid === groupUid);
  if (group) void removeEvaluationFromGroup(group, evaluation);
}

/** A group's assigned evaluations, as removable rows carrying their type chip. */
function groupMemberItems(group: PlaybookEvaluationGroupInDb): GaiaRemovableItem[] {
  return evaluationsForGroup(group).map(evaluation => ({
    key: evaluation.uid,
    label: evaluationDisplayName(evaluation),
    meta: evaluationTagLabel(evaluation),
  }));
}

/** Actions hanging off one trigger, as removable rows led by their type icon. */
function actionItems(actions: PlaybookOptionAction[]): GaiaRemovableItem[] {
  return actions.map(action => ({
    key: action.uid,
    label: action.name,
    icon: actionTypeIcon(action.action_type),
  }));
}

function findAction(actions: PlaybookOptionAction[], uid: string): PlaybookOptionAction | null {
  return actions.find(action => action.uid === uid) ?? null;
}
const waitingForGeneratedEvaluations = computed<boolean>(
  () =>
    Boolean(createdPlaybook.value) &&
    createdPlaybook.value?.generate_with_ai === true &&
    !playbookUpdatedReceived.value &&
    !hasEvaluations.value &&
    !evaluationsLoading.value &&
    !evaluationsError.value
);

const continueDisabled = computed<boolean>(() => {
  if (activeStep.value === 'setup') {
    return !setupComplete.value || submitting.value || loadingPlaybook.value;
  }
  if (activeStep.value === 'evaluations') {
    return classificationEvaluationsMissingOptions.value.length > 0;
  }
  return false;
});

const continueLabel = computed<string>(() => {
  if (activeStep.value === 'setup' && !isEdit.value && !createdPlaybook.value)
    return 'Create playbook';
  const next = nextStep.value;
  if (next) return `Continue to ${next.title.toLowerCase()}`;
  return isEdit.value ? 'Save changes' : 'Finish setup';
});

const activeStepDefinition = computed<StepDefinition>(
  () => steps[currentStepIndex.value] ?? steps[0]
);

/** Items the rail counts for a step — the same lists the step body renders. */
function stepItemCount(key: StepKey): number {
  switch (key) {
    case 'extraction':
      return extractionEvaluations.value.length;
    case 'evaluations':
      return classificationEvaluations.value.length;
    case 'uncommons':
      return uncommonsCheckEvaluations.value.length;
    case 'clauses':
      return clauseValidationEvaluations.value.length;
    case 'groups':
      return evaluationGroups.value.length;
    default:
      return 0;
  }
}

const railItems = computed<GaiaStepRailItem[]>(() =>
  steps.map(step => ({
    key: step.key,
    label: step.title,
    description: step.description,
    locked: !isStepAccessible(step.key),
    // Setup has no list of its own, and a locked step has nothing to count yet.
    count:
      step.key === 'setup' || !isStepAccessible(step.key) ? undefined : stepItemCount(step.key),
  }))
);

const itemsCountLabel = computed<string>(
  () => `${stepItemCount(activeStep.value)} ${activeStepDefinition.value.title.toLowerCase()}`
);

// The content of a generated playbook is worth marking as such, so a user coming
// back to it knows the rows were written for them rather than by them.
const showGeneratedBadge = computed<boolean>(
  () =>
    createdPlaybook.value?.generate_with_ai === true &&
    activeStep.value !== 'setup' &&
    stepItemCount(activeStep.value) > 0
);

const footerNote = computed<string | undefined>(() => {
  if (activeStep.value === 'setup') {
    return setupValidationMessage.value ?? 'Every section stays editable after creation.';
  }
  return 'Everything here stays editable after the playbook is created.';
});

/** The status field is a segmented control in the mock; archived stays reachable. */
const statusSegmentOptions = computed(() =>
  statusOptions.map(option => ({ label: String(option.label), value: String(option.value) }))
);

function isStepAccessible(key: StepKey): boolean {
  if (key === 'setup') return true;
  return Boolean(createdPlaybook.value);
}

function moveToStep(key: StepKey): void {
  if (!isStepAccessible(key)) return;
  navigateToStep(key);
}

function goBack(): void {
  const index = currentStepIndex.value;
  if (index <= 0) return;
  navigateToStep(steps[index - 1].key);
}

function navigateBack(): void {
  void router.push({ name: 'playbooks' });
}

function navigateToStep(key: StepKey): void {
  const uid = createdPlaybook.value?.uid ?? routedPlaybookUid.value;
  const editing = isEdit.value;
  if (key === 'setup') {
    if (editing && uid) {
      void router.push(playbookEditRoute(uid));
      return;
    }
    void router.push(
      uid ? { name: 'playbook-create', params: { uid } } : { name: 'playbook-create' }
    );
    return;
  }

  if (!uid) return;
  const routeName =
    key === 'clauses'
      ? 'playbook-clauses'
      : key === 'uncommons'
        ? 'playbook-uncommons'
        : key === 'extraction'
          ? 'playbook-extractions'
          : key === 'groups'
            ? 'playbook-groups'
            : 'playbook-evaluations';
  void router.push({
    name: routeName,
    params: { uid },
    // Carry the edit flag so subsequent steps also render in edit mode.
    ...(editing ? { query: { edit: 'true' } } : {}),
  });
}

function resetEvaluationState(): void {
  evaluations.value = [];
  evaluationsError.value = null;
  evaluationGroups.value = [];
  playbookUpdatedReceived.value = false;
  for (const key of Object.keys(optionDrafts)) {
    delete optionDrafts[key];
  }
  for (const key of Object.keys(optionSaving)) {
    delete optionSaving[key];
  }
  for (const key of Object.keys(evaluationDeleting)) {
    delete evaluationDeleting[key];
  }
  for (const key of Object.keys(collapsedEvaluations)) {
    delete collapsedEvaluations[key];
  }
  closeClassificationOptionsModal();
}

function applyPlaybook(playbook: PlaybookInDb): void {
  createdPlaybook.value = playbook;
  form.name = playbook.name;
  form.contract_type_uids = playbook.contract_types.map(type => type.uid);
  form.party_position = playbook.party_position;
  form.status = playbook.status;
  form.language = playbook.language;
  form.description = playbook.description ?? null;
  form.summary_prompt = playbook.summary_prompt ?? null;
  form.add_clauses_to_library = playbook.add_clauses_to_library;
  // The summary prompt is folded away by default; a playbook that already has
  // one must not hide it behind a link the user has no reason to click.
  if (form.summary_prompt) showSummaryPrompt.value = true;
}

function eventPayloadUid(event: GaiaEvent): string | null {
  const data = event.data;
  if (typeof data !== 'object' || !('uid' in data)) {
    return null;
  }
  const uid = (data as { uid?: unknown }).uid;
  return typeof uid === 'string' ? uid : null;
}

function formatEnumLabel(value: string | null | undefined): string {
  if (!value) return '-';
  return value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function evaluationTagLabel(evaluation: PlaybookEvaluation): string {
  if (evaluation.evaluation_type === 'clause_validation') {
    return 'Clause validation';
  }
  if (evaluation.evaluation_type === 'uncommons_check') {
    return 'Uncommons check';
  }
  if (evaluation.evaluation_type === 'extraction') {
    return extractionTypeLabel(extractionResponseType(evaluation));
  }
  return evaluation.multiple_choices ? 'Multiple choice' : 'Single choice';
}

function evaluationDisplayName(evaluation: PlaybookEvaluation): string {
  return evaluationLinkedClause(evaluation)?.name ?? evaluation.name;
}

function clearEvaluationLocalState(evaluation: PlaybookEvaluation): void {
  if (activeEvaluationUid.value === evaluation.uid) {
    activeEvaluationUid.value = null;
  }
  delete evaluationDeleting[evaluation.uid];
  delete collapsedEvaluations[evaluation.uid];

  const draftPrefix = `${evaluation.uid}:`;
  for (const key of Object.keys(optionDrafts)) {
    if (key.startsWith(draftPrefix)) {
      delete optionDrafts[key];
    }
  }
  for (const key of Object.keys(optionSaving)) {
    if (key.startsWith(draftPrefix)) {
      delete optionSaving[key];
    }
  }

  if (classificationOptionsModalEvaluation.value?.uid === evaluation.uid) {
    closeClassificationOptionsModal();
  }

  if (uncommonsModalEvaluation.value?.uid === evaluation.uid) {
    closeUncommonsModal();
  }
}

function isClauseValidationEvaluation(evaluation: PlaybookEvaluation): boolean {
  return evaluation.evaluation_type === 'clause_validation';
}

function isUncommonsEvaluation(evaluation: PlaybookEvaluation): boolean {
  return evaluation.evaluation_type === 'uncommons_check';
}

function isExtractionEvaluation(evaluation: PlaybookEvaluation): boolean {
  return evaluation.evaluation_type === 'extraction';
}

function evaluationLinkedClause(evaluation: PlaybookEvaluation): ClauseSlim | null {
  const option = evaluation.options?.find(
    candidate => 'clause' in candidate && Boolean(candidate.clause)
  );
  if (!option || !('clause' in option) || !option.clause) {
    return null;
  }
  return option.clause;
}

function clauseValidationContent(evaluation: PlaybookEvaluation): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return evaluationLinkedClause(evaluation)?.content || 'No linked clause content was returned.';
}

function clauseMetadata(evaluation: PlaybookEvaluation): { label: string; value: string }[] {
  const clause = evaluationLinkedClause(evaluation);
  const values = [
    { label: 'Clause type', value: clause?.clause_category ?? null },
    { label: 'Jurisdiction', value: clause?.jurisdiction ?? null },
    {
      label: 'Contract types',
      value: clause?.contract_types?.length
        ? contractTypeNames(clause.contract_types).join(', ')
        : null,
    },
    { label: 'Language', value: clause?.language ?? null },
    { label: 'Party position', value: createdPlaybook.value?.party_position ?? null },
  ];

  return values
    .filter((item): item is { label: string; value: string } => Boolean(item.value))
    .map(item => ({ ...item, value: formatEnumLabel(item.value) }));
}

function optionForType(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType
): PlaybookEvaluationOption | null {
  return (
    evaluation.options?.find(
      option => 'option_type' in option && option.option_type === optionType
    ) ?? null
  );
}

function optionDraftKey(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType
): string {
  return `${evaluation.uid}:${optionType}`;
}

function defaultOptionTypeDraft(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType
): string {
  const option = optionForType(evaluation, optionType);
  if (option) {
    return option.definition;
  }

  return optionType === ClauseValidationOptionType.PREFERRED
    ? clauseValidationContent(evaluation)
    : '';
}

function optionTypeDraft(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType
): string {
  const key = optionDraftKey(evaluation, optionType);
  if (!(key in optionDrafts)) {
    optionDrafts[key] = defaultOptionTypeDraft(evaluation, optionType);
  }
  return optionDrafts[key];
}

function setOptionTypeDraft(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType,
  value: string
): void {
  optionDrafts[optionDraftKey(evaluation, optionType)] = value;
}

function applySavedEvaluationOption(
  evaluationUid: string,
  savedOption: PlaybookEvaluationOption
): void {
  const evaluationIndex = evaluations.value.findIndex(
    evaluation => evaluation.uid === evaluationUid
  );
  if (evaluationIndex === -1) return;

  const evaluation = evaluations.value[evaluationIndex];
  const options = [...(evaluation.options ?? [])] as PlaybookEvaluationOption[];
  const savedOptionType = 'option_type' in savedOption ? savedOption.option_type : null;
  const optionIndex = options.findIndex(option => {
    if (option.uid === savedOption.uid) return true;
    return Boolean(
      savedOptionType && 'option_type' in option && option.option_type === savedOptionType
    );
  });

  if (optionIndex === -1) {
    options.push(savedOption);
  } else {
    options.splice(optionIndex, 1, savedOption);
  }

  evaluations.value.splice(evaluationIndex, 1, {
    ...evaluation,
    options,
  } as PlaybookEvaluation);
}

function applySavedEvaluation(savedEvaluation: PlaybookEvaluation): void {
  const evaluationIndex = evaluations.value.findIndex(
    evaluation => evaluation.uid === savedEvaluation.uid
  );
  if (evaluationIndex !== -1) {
    evaluations.value.splice(evaluationIndex, 1, savedEvaluation);
  }
}

function openCreateUncommonsEvaluation(): void {
  if (!createdPlaybook.value) {
    message.error('Create the playbook before adding uncommons checks.');
    return;
  }
  uncommonsModalMode.value = 'create';
  uncommonsModalEvaluation.value = null;
  uncommonsModalName.value = '';
  uncommonsModalPrompt.value = '';
  showUncommonsModal.value = true;
}

function openEditUncommonsEvaluation(evaluation: PlaybookEvaluation): void {
  uncommonsModalMode.value = 'edit';
  uncommonsModalEvaluation.value = evaluation;
  uncommonsModalName.value = evaluation.name;
  uncommonsModalPrompt.value = evaluation.extraction_prompt;
  showUncommonsModal.value = true;
}

function closeUncommonsModal(): void {
  showUncommonsModal.value = false;
  uncommonsModalMode.value = 'create';
  uncommonsModalEvaluation.value = null;
  uncommonsModalName.value = '';
  uncommonsModalPrompt.value = '';
  uncommonsModalSaving.value = false;
}

async function saveUncommonsModal(): Promise<void> {
  const isCreate = uncommonsModalMode.value === 'create';
  const evaluation = uncommonsModalEvaluation.value;
  if (!isCreate && !evaluation) return;

  const name = uncommonsModalName.value.trim();
  if (!name) {
    message.error('Enter a name before saving.');
    return;
  }

  const prompt = uncommonsModalPrompt.value.trim();
  if (!prompt) {
    message.error('Enter a prompt before saving.');
    return;
  }

  uncommonsModalSaving.value = true;
  try {
    if (isCreate) {
      const playbookUid = createdPlaybook.value?.uid;
      if (!playbookUid) {
        message.error('Create the playbook before adding uncommons checks.');
        return;
      }
      const newEvaluation = await PlaybooksService.createEvaluation(playbookUid, {
        evaluation_type: PlaybookEvaluationType.UNCOMMONS_CHECK,
        name,
        extraction_prompt: prompt,
      });
      evaluations.value = [newEvaluation, ...evaluations.value];
      collapsedEvaluations[newEvaluation.uid] = false;
      activeEvaluationUid.value = newEvaluation.uid;
      playbookUpdatedReceived.value = true;
      message.success(`${name} created.`);
      closeUncommonsModal();
      return;
    }

    if (!evaluation) return;
    const savedEvaluation = await PlaybooksService.updateEvaluation(evaluation.uid, {
      name,
      extraction_prompt: prompt,
    });
    applySavedEvaluation(savedEvaluation);
    message.success('Uncommons check saved.');
    closeUncommonsModal();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to save uncommons check.'));
  } finally {
    uncommonsModalSaving.value = false;
  }
}

function openCreateExtractionEvaluation(): void {
  if (!createdPlaybook.value) {
    message.error('Create the playbook before adding extractions.');
    return;
  }
  extractionModalMode.value = 'create';
  extractionModalEvaluation.value = null;
  extractionModalName.value = '';
  extractionModalPrompt.value = '';
  extractionModalResponseType.value = PlaybookExtractionResponseType.STR;
  showExtractionModal.value = true;
}

function openEditExtractionEvaluation(evaluation: PlaybookEvaluation): void {
  extractionModalMode.value = 'edit';
  extractionModalEvaluation.value = evaluation;
  extractionModalName.value = evaluation.name;
  extractionModalPrompt.value = evaluation.extraction_prompt;
  extractionModalResponseType.value =
    extractionResponseType(evaluation) ?? PlaybookExtractionResponseType.STR;
  showExtractionModal.value = true;
}

function closeExtractionModal(): void {
  showExtractionModal.value = false;
  extractionModalMode.value = 'create';
  extractionModalEvaluation.value = null;
  extractionModalName.value = '';
  extractionModalPrompt.value = '';
  extractionModalResponseType.value = PlaybookExtractionResponseType.STR;
  extractionModalSaving.value = false;
}

async function saveExtractionModal(): Promise<void> {
  const isCreate = extractionModalMode.value === 'create';
  const evaluation = extractionModalEvaluation.value;
  if (!isCreate && !evaluation) return;

  const name = extractionModalName.value.trim();
  if (!name) {
    message.error('Enter a name before saving.');
    return;
  }

  const prompt = extractionModalPrompt.value.trim();
  if (!prompt) {
    message.error('Enter a prompt before saving.');
    return;
  }

  extractionModalSaving.value = true;
  try {
    if (isCreate) {
      const playbookUid = createdPlaybook.value?.uid;
      if (!playbookUid) {
        message.error('Create the playbook before adding extractions.');
        return;
      }
      const newEvaluation = await PlaybooksService.createEvaluation(playbookUid, {
        evaluation_type: PlaybookEvaluationType.EXTRACTION,
        name,
        extraction_prompt: prompt,
        response_type: extractionModalResponseType.value,
      });
      evaluations.value = [newEvaluation, ...evaluations.value];
      collapsedEvaluations[newEvaluation.uid] = false;
      activeEvaluationUid.value = newEvaluation.uid;
      playbookUpdatedReceived.value = true;
      message.success(`${name} created.`);
      closeExtractionModal();
      return;
    }

    if (!evaluation) return;
    // response_type is immutable after creation (not part of PlaybookEvaluationUpdate).
    const savedEvaluation = await PlaybooksService.updateEvaluation(evaluation.uid, {
      name,
      extraction_prompt: prompt,
    });
    applySavedEvaluation(savedEvaluation);
    message.success('Extraction saved.');
    closeExtractionModal();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to save extraction.'));
  } finally {
    extractionModalSaving.value = false;
  }
}

function openCreateClassificationEvaluation(): void {
  if (!createdPlaybook.value) {
    message.error('Create the playbook before adding classifications.');
    return;
  }
  classificationModalMode.value = 'create';
  classificationOptionsModalEvaluation.value = null;
  classificationOptionsModalName.value = '';
  classificationOptionsModalPrompt.value = '';
  classificationOptionsModalMultiple.value = false;
  classificationOptionsModalRows.value = [];
  classificationOptionsRemovedUids.value = [];
  showClassificationOptionsModal.value = true;
}

function openClassificationOptionsModal(evaluation: PlaybookEvaluation): void {
  classificationModalMode.value = 'edit';
  classificationOptionsModalEvaluation.value = evaluation;
  classificationOptionsModalName.value = evaluation.name;
  classificationOptionsModalPrompt.value = evaluation.extraction_prompt;
  classificationOptionsModalMultiple.value =
    'multiple_choices' in evaluation ? evaluation.multiple_choices : false;
  classificationOptionsRemovedUids.value = [];
  classificationOptionsModalRows.value = (evaluation.options ?? []).map(option => ({
    key: option.uid,
    uid: option.uid,
    name: option.name,
    definition: option.definition,
    color: option.color ?? null,
    riskLevel: option.risk_level ?? null,
  }));
  showClassificationOptionsModal.value = true;
}

function closeClassificationOptionsModal(): void {
  showClassificationOptionsModal.value = false;
  classificationModalMode.value = 'edit';
  classificationOptionsModalEvaluation.value = null;
  classificationOptionsModalName.value = '';
  classificationOptionsModalPrompt.value = '';
  classificationOptionsModalMultiple.value = false;
  classificationOptionsModalRows.value = [];
  classificationOptionsRemovedUids.value = [];
  classificationOptionsModalSaving.value = false;
}

function addClassificationOptionModalRow(): void {
  newClassificationOptionIndex += 1;
  classificationOptionsModalRows.value.push({
    key: `new-${newClassificationOptionIndex}`,
    uid: null,
    name: '',
    definition: '',
    color: '#dbeafe',
    riskLevel: null,
  });
}

function removeClassificationOptionModalRow(row: ClassificationOptionDraft): void {
  if (row.uid) {
    classificationOptionsRemovedUids.value.push(row.uid);
  }
  classificationOptionsModalRows.value = classificationOptionsModalRows.value.filter(
    candidate => candidate.key !== row.key
  );
}

async function saveClassificationOptionsModal(): Promise<void> {
  const isCreate = classificationModalMode.value === 'create';
  const evaluation = classificationOptionsModalEvaluation.value;
  if (!isCreate && !evaluation) return;

  const name = classificationOptionsModalName.value.trim();
  if (!name) {
    message.error('Enter a name before saving.');
    return;
  }

  const prompt = classificationOptionsModalPrompt.value.trim();
  if (!prompt) {
    message.error('Enter a prompt before saving.');
    return;
  }

  const rows = classificationOptionsModalRows.value.map(row => ({
    ...row,
    name: row.name.trim(),
    definition: row.definition.trim(),
  }));

  if (rows.length === 0) {
    message.error('Add at least one option before saving.');
    return;
  }

  if (rows.some(row => !row.name || !row.definition)) {
    message.error('Enter a name and definition for each option before saving.');
    return;
  }

  classificationOptionsModalSaving.value = true;
  try {
    if (isCreate) {
      const playbookUid = createdPlaybook.value?.uid;
      if (!playbookUid) {
        message.error('Create the playbook before adding classifications.');
        return;
      }

      const newEvaluation = await PlaybooksService.createEvaluation(playbookUid, {
        evaluation_type: PlaybookEvaluationType.CLASSIFICATION,
        name,
        extraction_prompt: prompt,
        multiple_choices: classificationOptionsModalMultiple.value,
      });
      evaluations.value = [newEvaluation, ...evaluations.value];

      const savedOptions: PlaybookEvaluationOption[] = [];
      for (const row of rows) {
        const savedOption = await PlaybooksService.createOption(newEvaluation.uid, {
          name: row.name,
          definition: row.definition,
          color: row.color,
          risk_level: row.riskLevel,
        });
        savedOptions.push(savedOption);
      }

      const evaluationIndex = evaluations.value.findIndex(
        candidate => candidate.uid === newEvaluation.uid
      );
      if (evaluationIndex !== -1) {
        evaluations.value.splice(evaluationIndex, 1, {
          ...evaluations.value[evaluationIndex],
          options: savedOptions,
        } as PlaybookEvaluation);
      }

      collapsedEvaluations[newEvaluation.uid] = false;
      activeEvaluationUid.value = newEvaluation.uid;
      playbookUpdatedReceived.value = true;
      message.success(`${name} created.`);
      closeClassificationOptionsModal();
      return;
    }

    if (!evaluation) return;
    const savedEvaluation = await PlaybooksService.updateEvaluation(evaluation.uid, {
      name,
      extraction_prompt: prompt,
    });
    applySavedEvaluation(savedEvaluation);

    const savedOptions: PlaybookEvaluationOption[] = [];
    for (const row of rows) {
      const payload = {
        name: row.name,
        definition: row.definition,
        color: row.color,
        risk_level: row.riskLevel,
      };
      const savedOption = row.uid
        ? await PlaybooksService.updateOption(row.uid, payload)
        : await PlaybooksService.createOption(evaluation.uid, payload);
      savedOptions.push(savedOption);
    }

    for (const removedUid of classificationOptionsRemovedUids.value) {
      await PlaybooksService.deleteOption(removedUid);
    }

    const evaluationIndex = evaluations.value.findIndex(
      candidate => candidate.uid === evaluation.uid
    );
    if (evaluationIndex !== -1) {
      evaluations.value.splice(evaluationIndex, 1, {
        ...evaluations.value[evaluationIndex],
        name: savedEvaluation.name,
        extraction_prompt: savedEvaluation.extraction_prompt,
        options: savedOptions,
      } as PlaybookEvaluation);
    }

    message.success('Evaluation saved.');
    closeClassificationOptionsModal();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to save options.'));
  } finally {
    classificationOptionsModalSaving.value = false;
  }
}

function optionTypePlaceholder(optionType: ClauseValidationOptionType): string {
  return `No ${optionTypeLabelText(optionType).toLowerCase()} defined.`;
}

function optionTypeColor(optionType: ClauseValidationOptionType): string {
  switch (optionType) {
    case ClauseValidationOptionType.PREFERRED:
      return '#22c55e';
    case ClauseValidationOptionType.FALLBACK:
      return '#f59e0b';
    case ClauseValidationOptionType.ESCALATION:
      return '#ef4444';
    default:
      return '#dbeafe';
  }
}

function optionTypeRiskLevel(optionType: ClauseValidationOptionType): PlaybookRiskLevel {
  switch (optionType) {
    case ClauseValidationOptionType.PREFERRED:
      return PlaybookRiskLevel.LOW;
    case ClauseValidationOptionType.FALLBACK:
      return PlaybookRiskLevel.MEDIUM;
    case ClauseValidationOptionType.ESCALATION:
      return PlaybookRiskLevel.HIGH;
    default:
      return PlaybookRiskLevel.LOW;
  }
}

function optionTypeLabelText(optionType: ClauseValidationOptionType): string {
  return clauseOptionTypeLabel(optionType);
}

async function saveClauseValidationOption(
  evaluation: PlaybookEvaluation,
  optionType: ClauseValidationOptionType
): Promise<void> {
  const key = optionDraftKey(evaluation, optionType);
  const definition = (optionDrafts[key] ?? defaultOptionTypeDraft(evaluation, optionType)).trim();
  if (!definition) {
    message.error(`Enter ${optionTypeLabelText(optionType).toLowerCase()} before saving.`);
    return;
  }

  optionSaving[key] = true;
  try {
    const existingOption = optionForType(evaluation, optionType);
    const linkedClause = evaluationLinkedClause(evaluation);
    const payload = {
      name: optionTypeLabelText(optionType),
      definition,
      color: optionTypeColor(optionType),
      risk_level: optionTypeRiskLevel(optionType),
      option_type: optionType,
      clause_uid: linkedClause?.uid ?? null,
    };

    let savedOption: PlaybookEvaluationOption;
    if (existingOption) {
      savedOption = await PlaybooksService.updateOption(existingOption.uid, payload);
    } else {
      savedOption = await PlaybooksService.createOption(evaluation.uid, payload);
    }

    applySavedEvaluationOption(evaluation.uid, savedOption);
    optionDrafts[key] = savedOption.definition;
    // The option's trigger (which actions attach to) is created server-side
    // alongside the option. If it isn't present on the response yet, refetch so
    // the "Add action" button reflects the trigger and enables.
    if (!savedOption.trigger_uid) {
      await fetchPlaybookEvaluations();
    }
    message.success(`${optionTypeLabelText(optionType)} saved.`);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to save playbook option.'));
  } finally {
    optionSaving[key] = false;
  }
}

function openSourceClauseEdit(evaluation: PlaybookEvaluation): void {
  const clause = evaluationLinkedClause(evaluation);
  if (!clause) {
    message.error('No linked clause is available to edit.');
    return;
  }
  clauseEditMode.value = 'edit';
  editingSourceClause.value = clause;
  showClauseEditModal.value = true;
}

function openAddClauseEvaluation(): void {
  if (!createdPlaybook.value) {
    message.error('Create the playbook before adding clauses.');
    return;
  }
  clauseEditMode.value = 'create';
  editingSourceClause.value = null;
  showClauseEditModal.value = true;
}

function openImportClauseEvaluation(): void {
  if (!createdPlaybook.value) {
    message.error('Create the playbook before adding clauses.');
    return;
  }
  showClausePickerModal.value = true;
}

async function handleClausePicked(clause: ClauseInDb): Promise<void> {
  await addClauseValidationEvaluation(clause);
}

async function addClauseValidationEvaluation(clause: ClauseSlim | LibraryClause): Promise<void> {
  const playbookUid = createdPlaybook.value?.uid;
  if (!playbookUid) {
    message.error('Create the playbook before adding clauses.');
    return;
  }
  if (importedClauseUids.value.includes(clause.uid)) {
    message.warning(`${clause.name} is already in this playbook.`);
    return;
  }

  addingClauseEvaluation.value = true;
  try {
    const newEvaluation = await PlaybooksService.createEvaluation(playbookUid, {
      evaluation_type: PlaybookEvaluationType.CLAUSE_VALIDATION,
      name: clause.name,
      extraction_prompt: `Find the ${clause.name} clause in the document.`,
    });
    evaluations.value = [newEvaluation, ...evaluations.value];

    const savedOption = (await PlaybooksService.createOption(newEvaluation.uid, {
      name: optionTypeLabelText(ClauseValidationOptionType.PREFERRED),
      definition: clause.content,
      color: optionTypeColor(ClauseValidationOptionType.PREFERRED),
      risk_level: optionTypeRiskLevel(ClauseValidationOptionType.PREFERRED),
      option_type: ClauseValidationOptionType.PREFERRED,
      clause_uid: clause.uid,
    })) as PlaybookEvaluationOption;

    applySavedEvaluationOption(newEvaluation.uid, savedOption);
    optionDrafts[optionDraftKey(newEvaluation, ClauseValidationOptionType.PREFERRED)] =
      savedOption.definition;
    collapsedEvaluations[newEvaluation.uid] = false;
    activeEvaluationUid.value = newEvaluation.uid;
    playbookUpdatedReceived.value = true;
    // The Preferred option's trigger (which actions attach to) and linked clause are created
    // server-side but may be absent from the create response. Refetch so "Add action" enables
    // and the duplicate-import guard sees the clause without requiring a manual re-save.
    if (!savedOption.trigger_uid || !('clause' in savedOption && savedOption.clause)) {
      await fetchPlaybookEvaluations();
    }
    message.success(`${clause.name} added.`);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to add clause to playbook.'));
  } finally {
    addingClauseEvaluation.value = false;
  }
}

async function handleClauseModalSaved(clause: LibraryClause): Promise<void> {
  showClauseEditModal.value = false;
  if (clauseEditMode.value === 'create') {
    editingSourceClause.value = null;
    await addClauseValidationEvaluation(clause);
    return;
  }

  editingSourceClause.value = null;
  await fetchPlaybookEvaluations();
}

async function deleteEvaluation(evaluation: PlaybookEvaluation): Promise<void> {
  evaluationDeleting[evaluation.uid] = true;
  try {
    await PlaybooksService.deleteEvaluation(evaluation.uid);
    evaluations.value = evaluations.value.filter(candidate => candidate.uid !== evaluation.uid);
    clearEvaluationLocalState(evaluation);
    message.success(`${evaluationDisplayName(evaluation)} deleted.`);
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete playbook evaluation.'));
  } finally {
    delete evaluationDeleting[evaluation.uid];
  }
}

function optionClauseName(option: PlaybookEvaluationOption): string | null {
  if ('clause' in option && option.clause) {
    return option.clause.name;
  }
  return null;
}

function optionActions(option: PlaybookEvaluationOption | null): PlaybookOptionAction[] {
  return option?.actions ?? [];
}

const reusableActions = ref<{ uid: string; label: string; action: PlaybookOptionAction }[]>([]);

async function loadReusableActions(): Promise<void> {
  const playbookUid = createdPlaybook.value?.uid;
  if (!playbookUid) {
    reusableActions.value = [];
    return;
  }
  try {
    const actions = await PlaybooksService.listActions(playbookUid);
    reusableActions.value = actions.map(action => ({
      uid: action.uid,
      label: action.name,
      action,
    }));
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to load reusable actions.'));
  }
}

function openCreateAction(
  triggerUid: string | null | undefined,
  context?: { evaluationUid?: string; optionUid?: string; scope?: ActionTriggerScope }
): void {
  actionModalMode.value = 'create';
  actionModalTriggerUid.value = triggerUid ?? null;
  actionModalEvaluationUid.value = context?.evaluationUid ?? null;
  actionModalOptionUid.value = context?.optionUid ?? null;
  actionModalAction.value = null;
  actionModalTriggerScope.value = context?.scope ?? 'evaluation';
  showActionModal.value = true;
  void loadReusableActions();
}

function openCreateActionForOption(
  evaluation: PlaybookEvaluation,
  option: PlaybookEvaluationOption | null
): void {
  if (!option) return;
  openCreateAction(option.trigger_uid, {
    evaluationUid: evaluation.uid,
    optionUid: option.uid,
  });
}

function openEditAction(
  triggerUid: string | null | undefined,
  action: PlaybookOptionAction,
  scope: ActionTriggerScope = 'evaluation'
): void {
  actionModalMode.value = 'edit';
  actionModalTriggerUid.value = triggerUid ?? null;
  actionModalEvaluationUid.value = null;
  actionModalOptionUid.value = null;
  actionModalAction.value = action;
  actionModalTriggerScope.value = scope;
  showActionModal.value = true;
}

async function handleActionSaved(): Promise<void> {
  showActionModal.value = false;
  actionModalAction.value = null;
  await fetchPlaybookEvaluations();
  void fetchEvaluationGroups();
}

async function deleteAction(action: PlaybookOptionAction): Promise<void> {
  actionDeleting[action.uid] = true;
  try {
    await PlaybooksService.deleteAction(action.uid);
    await fetchPlaybookEvaluations();
    void fetchEvaluationGroups();
    message.success('Action deleted.');
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete action.'));
  } finally {
    delete actionDeleting[action.uid];
  }
}

function evaluationTriggers(evaluation: PlaybookEvaluation): PlaybookFindingThresholdTrigger[] {
  return findingThresholdTriggers(evaluation);
}

function triggerActions(trigger: PlaybookFindingThresholdTrigger): PlaybookOptionAction[] {
  return trigger.actions ?? [];
}

function openCreateTrigger(evaluation: PlaybookEvaluation): void {
  triggerModalMode.value = 'create';
  triggerModalEvaluationUid.value = evaluation.uid;
  triggerModalTrigger.value = null;
  showTriggerModal.value = true;
}

function openEditTrigger(
  evaluation: PlaybookEvaluation,
  trigger: PlaybookFindingThresholdTrigger
): void {
  triggerModalMode.value = 'edit';
  triggerModalEvaluationUid.value = evaluation.uid;
  triggerModalTrigger.value = trigger;
  showTriggerModal.value = true;
}

async function handleTriggerSaved(): Promise<void> {
  showTriggerModal.value = false;
  triggerModalTrigger.value = null;
  await fetchPlaybookEvaluations();
}

async function deleteTrigger(trigger: PlaybookFindingThresholdTrigger): Promise<void> {
  triggerDeleting[trigger.uid] = true;
  try {
    await PlaybooksService.deleteTrigger(trigger.uid);
    await fetchPlaybookEvaluations();
    message.success('Finding trigger deleted.');
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete finding trigger.'));
  } finally {
    delete triggerDeleting[trigger.uid];
  }
}

async function fetchPlaybookEvaluations(): Promise<void> {
  const playbookUid = createdPlaybook.value?.uid;
  if (!playbookUid || evaluationsLoading.value) return;

  evaluationsLoading.value = true;
  evaluationsError.value = null;
  try {
    const result = await PlaybooksService.listEvaluations(playbookUid);
    if (createdPlaybook.value?.uid !== playbookUid) return;
    evaluations.value = result;
    if (result.length > 0) {
      playbookUpdatedReceived.value = true;
    }
  } catch (error_) {
    evaluationsError.value = extractErrorDetail(error_, 'Failed to load playbook evaluations.');
  } finally {
    evaluationsLoading.value = false;
  }
}

async function fetchEvaluationGroups(): Promise<void> {
  const playbookUid = createdPlaybook.value?.uid;
  if (!playbookUid) return;
  try {
    const result = await PlaybooksService.listEvaluationGroups(playbookUid);
    if (createdPlaybook.value?.uid !== playbookUid) return;
    evaluationGroups.value = result;
  } catch {
    evaluationGroups.value = [];
  }
}

function handleGroupOptionSelect(key: string, evaluation: PlaybookEvaluation): void {
  if (key === CREATE_GROUP_KEY) {
    openCreateGroupModal(evaluation);
    return;
  }
  void addEvaluationToGroup(key, evaluation);
}

async function addEvaluationToGroup(
  groupUid: string,
  evaluation: PlaybookEvaluation
): Promise<void> {
  try {
    await PlaybooksService.addEvaluationGroupEvaluation(groupUid, evaluation.uid);
    message.success('Evaluation added to group.');
    await fetchEvaluationGroups();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to add evaluation to group.'));
  }
}

async function removeEvaluationFromGroup(
  group: PlaybookEvaluationGroupInDb,
  evaluation: PlaybookEvaluation
): Promise<void> {
  try {
    await PlaybooksService.removeEvaluationGroupEvaluation(group.uid, evaluation.uid);
    message.success('Evaluation removed from group.');
    await fetchEvaluationGroups();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to remove evaluation from group.'));
  }
}

function openCreateGroupModal(evaluation: PlaybookEvaluation | null = null): void {
  groupTargetEvaluation.value = evaluation;
  newGroupName.value = '';
  showCreateGroupModal.value = true;
}

function closeCreateGroupModal(): void {
  showCreateGroupModal.value = false;
  groupTargetEvaluation.value = null;
  newGroupName.value = '';
}

// Focus the name field once the modal has finished opening (replaces the
// `autofocus` prop, which the a11y linter disallows).
function focusNewGroupNameInput(): void {
  newGroupNameInput.value?.focus();
}

async function submitCreateGroup(): Promise<void> {
  const evaluation = groupTargetEvaluation.value;
  const playbookUid = createdPlaybook.value?.uid;
  const name = newGroupName.value.trim();
  if (!playbookUid || !name || creatingGroup.value) return;

  creatingGroup.value = true;
  try {
    await PlaybooksService.createEvaluationGroup(playbookUid, {
      name,
      evaluation_uids: evaluation ? [evaluation.uid] : [],
    });
    message.success('Group created.');
    await fetchEvaluationGroups();
    closeCreateGroupModal();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to create group.'));
  } finally {
    creatingGroup.value = false;
  }
}

function openEditGroupModal(group: PlaybookEvaluationGroupInDb): void {
  editGroupTarget.value = group;
  editGroupName.value = group.name ?? '';
  showEditGroupModal.value = true;
}

function closeEditGroupModal(): void {
  showEditGroupModal.value = false;
  editGroupTarget.value = null;
  editGroupName.value = '';
}

function focusEditGroupNameInput(): void {
  editGroupNameInput.value?.focus();
}

async function submitEditGroup(): Promise<void> {
  const group = editGroupTarget.value;
  const name = editGroupName.value.trim();
  if (!group || !name || savingEditGroup.value) return;

  savingEditGroup.value = true;
  try {
    await PlaybooksService.updateEvaluationGroup(group.uid, { name });
    message.success('Group updated.');
    await fetchEvaluationGroups();
    closeEditGroupModal();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to update group.'));
  } finally {
    savingEditGroup.value = false;
  }
}

async function deleteGroup(group: PlaybookEvaluationGroupInDb): Promise<void> {
  groupDeleting[group.uid] = true;
  try {
    await PlaybooksService.deleteEvaluationGroup(group.uid);
    message.success('Group deleted.');
    if (activeGroupUid.value === group.uid) {
      activeGroupUid.value = null;
    }
    await fetchEvaluationGroups();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to delete group.'));
  } finally {
    delete groupDeleting[group.uid];
  }
}

// Eligible evaluations to add, grouped by evaluation type (extraction is not groupable).
function buildAddEvaluationOptions(group: PlaybookEvaluationGroupInDb): SelectGroupOption[] {
  const memberUids = new Set(group.evaluation_uids ?? []);
  const eligible = evaluations.value.filter(
    evaluation => !isExtractionEvaluation(evaluation) && !memberUids.has(evaluation.uid)
  );
  const typeOrder: PlaybookEvaluation['evaluation_type'][] = [
    'classification',
    'uncommons_check',
    'clause_validation',
  ];
  return typeOrder
    .map(type => ({
      type: 'group' as const,
      key: type,
      label: evaluationTypeLabel(type),
      children: eligible
        .filter(evaluation => evaluation.evaluation_type === type)
        .map(evaluation => ({ label: evaluationDisplayName(evaluation), value: evaluation.uid })),
    }))
    .filter(groupOption => groupOption.children.length > 0);
}

async function handleAddEvaluationToGroupSelect(
  evaluationUid: string | null,
  group: PlaybookEvaluationGroupInDb
): Promise<void> {
  if (!evaluationUid) return;
  try {
    await PlaybooksService.addEvaluationGroupEvaluation(group.uid, evaluationUid);
    message.success('Evaluation added to group.');
    await fetchEvaluationGroups();
  } catch (error_) {
    message.error(extractErrorDetail(error_, 'Failed to add evaluation to group.'));
  }
}

function openGroupTriggerModal(group: PlaybookEvaluationGroupInDb): void {
  groupTriggerTarget.value = group;
  showGroupTriggerModal.value = true;
}

async function handleGroupTriggerSaved(): Promise<void> {
  await fetchEvaluationGroups();
}

async function loadPlaybookFromRoute(uid: string): Promise<void> {
  if (createdPlaybook.value?.uid === uid) {
    subscribeToPlaybookUpdates(uid);
    if (!hasEvaluations.value && !evaluationsLoading.value) {
      await fetchPlaybookEvaluations();
    }
    void fetchEvaluationGroups();
    return;
  }

  loadingPlaybook.value = true;
  error.value = null;
  try {
    const playbook = await PlaybooksService.getById(uid);
    if (routedPlaybookUid.value !== uid) return;
    applyPlaybook(playbook);
    resetEvaluationState();
    subscribeToPlaybookUpdates(playbook.uid);
    await fetchPlaybookEvaluations();
    void fetchEvaluationGroups();
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to load playbook.');
  } finally {
    loadingPlaybook.value = false;
  }
}

const handlePlaybookUpdated: WebSocketEventHandler = event => {
  if (event.gaia_event_type !== GaiaEventType.PLAYBOOK_UPDATED) return;

  const payloadUid = eventPayloadUid(event);
  if (payloadUid && payloadUid !== createdPlaybook.value?.uid) return;

  playbookUpdatedReceived.value = true;
  void fetchPlaybookEvaluations();
  void fetchEvaluationGroups();
};

function unsubscribeFromPlaybookUpdates(): void {
  unsubscribePlaybookUpdates?.();
  unsubscribePlaybookUpdates = null;
}

function subscribeToPlaybookUpdates(playbookUid: string): void {
  unsubscribeFromPlaybookUpdates();
  if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
    logger.warn('[WebSocket] WebSocket is disabled via settings');
    return;
  }

  unsubscribePlaybookUpdates = getWsClient().subscribe(
    WebsocketSubscriptionTarget.PLAYBOOK,
    playbookUid,
    handlePlaybookUpdated
  );
}

async function resolveVaultFileUid(): Promise<string | null> {
  // A reference contract is optional. Return null when none is selected.
  if (sourceMode.value === 'vault') {
    return selectedVaultFileUid.value;
  }

  const panel = referenceUploadPanel.value;
  if (!panel || panel.files().length === 0) {
    return null;
  }

  // The panel shows the progress, and why an upload failed.
  const uploaded = await panel.upload();
  if (!uploaded) throw new Error('The reference contract could not be uploaded.');
  return uploaded[0]?.fileUid ?? null;
}

// Create the playbook from the selected code-defined template. It arrives populated, but the user
// continues through the wizard as with the other methods so the template's content can be edited.
async function createPlaybookFromTemplate(): Promise<void> {
  const templateKey = selectedTemplateKey.value;
  if (!templateKey || submitting.value) return;

  submitting.value = true;
  error.value = null;

  try {
    const playbook = await store.createPlaybookFromTemplate(templateKey);
    message.success('Playbook created.');
    void router.replace({ name: 'playbook-extractions', params: { uid: playbook.uid } });
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to create playbook from template.');
  } finally {
    submitting.value = false;
  }
}

async function createPlaybook(): Promise<void> {
  if (!setupComplete.value || submitting.value) return;

  if (isTemplateCreation.value) {
    await createPlaybookFromTemplate();
    return;
  }

  submitting.value = true;
  error.value = null;

  try {
    // Only resolve/upload the reference file when generating with AI; otherwise no source is sent.
    const userInput = isAiCreation.value
      ? {
          vault_file_uid: await resolveVaultFileUid(),
          generation_prompt: generationPrompt.value.trim() || null,
        }
      : null;
    const playbook = await store.createPlaybook({
      name: form.name.trim(),
      contract_type_uids: form.contract_type_uids,
      party_position: form.party_position,
      status: form.status,
      language: form.language.trim(),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      description: form.description?.trim() || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      summary_prompt: form.summary_prompt?.trim() || null,
      // The generation options only exist for the AI method, so they never leak into an
      // empty playbook created from scratch.
      add_clauses_to_library: isAiCreation.value && Boolean(form.add_clauses_to_library),
      generate_with_ai: isAiCreation.value,
      add_actions: isAiCreation.value && Boolean(form.add_actions),
      user_input: userInput,
    });
    applyPlaybook(playbook);
    resetEvaluationState();
    subscribeToPlaybookUpdates(playbook.uid);
    void router.replace({ name: 'playbook-extractions', params: { uid: playbook.uid } });
    void fetchPlaybookEvaluations();
    message.success('Playbook created.');
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to create playbook.');
  } finally {
    submitting.value = false;
  }
}

async function savePlaybookMetadata(): Promise<boolean> {
  const uid = createdPlaybook.value?.uid ?? routedPlaybookUid.value;
  if (!uid || submitting.value) return false;

  submitting.value = true;
  error.value = null;

  try {
    const playbook = await store.updatePlaybook(uid, {
      name: form.name.trim(),
      contract_type_uids: form.contract_type_uids,
      party_position: form.party_position,
      status: form.status,
      language: form.language.trim(),
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      description: form.description?.trim() || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      summary_prompt: form.summary_prompt?.trim() || null,
    });
    applyPlaybook(playbook);
    return true;
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to update playbook.');
    return false;
  } finally {
    submitting.value = false;
  }
}

async function activateDraftPlaybookOnFinish(): Promise<boolean> {
  const playbook = createdPlaybook.value;
  if (!playbook || playbook.status !== PlaybookStatus.DRAFT) return true;
  if (submitting.value) return false;

  submitting.value = true;
  error.value = null;

  try {
    const updatedPlaybook = await store.updatePlaybook(playbook.uid, {
      status: PlaybookStatus.ACTIVE,
    });
    applyPlaybook(updatedPlaybook);
    return true;
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to activate playbook.');
    return false;
  } finally {
    submitting.value = false;
  }
}

async function updatePlaybookMetadata(): Promise<void> {
  if (await savePlaybookMetadata()) {
    message.success('Playbook updated.');
    void router.push({ name: 'playbooks' });
  }
}

async function handleSaveChanges(): Promise<void> {
  if (activeStep.value === 'setup') {
    // Persists metadata, toasts, and navigates back to the playbooks list.
    await updatePlaybookMetadata();
    return;
  }
  // Non-setup edits already persist as each modal is confirmed, so there's nothing to flush here.
  message.success('Changes saved.');
  void router.push({ name: 'playbooks' });
}

async function handleContinue(): Promise<void> {
  if (activeStep.value === 'setup') {
    if (!isEdit.value && !createdPlaybook.value) {
      await createPlaybook();
      return;
    }
    // Editing, or revisiting setup during creation: persist metadata edits before moving on.
    if (await savePlaybookMetadata()) {
      const next = nextStep.value;
      if (next) navigateToStep(next.key);
    }
    return;
  }

  const next = nextStep.value;
  if (next) {
    navigateToStep(next.key);
    return;
  }

  if (!isEdit.value && !(await activateDraftPlaybookOnFinish())) return;

  void router.push({ name: 'playbooks' });
}

/*
 * Presentation helpers for the reworked wizard shell. Everything above stays the
 * source of truth for what the playbook is; these only shape it for the Gaia
 * components the steps are built from.
 */

const setupNameError = computed<boolean>(() => nameTouched.value && form.name.trim().length === 0);

const referenceSourceOptions = [
  { label: 'Upload', value: 'upload' },
  { label: 'Contract Vault', value: 'vault' },
];

function templateMetaLabel(template: PlaybookTemplateSummary): string {
  const counts = templateCountsLabel(template);
  return template.description ? `${template.description} · ${counts}` : counts;
}

/** Neutral chip after an item's title: its response type, or its evaluation kind. */
function itemTypeLabel(evaluation: PlaybookEvaluation): string | undefined {
  if (isClauseValidationEvaluation(evaluation)) {
    const category = evaluationLinkedClause(evaluation)?.clause_category;
    return category ? formatEnumLabel(category) : undefined;
  }
  return evaluationTagLabel(evaluation);
}

/*
 * Expand/collapse is keyed by uid, so groups and evaluations share one map —
 * the rows are the same GaiaPlaybookItem either way.
 */
function isCollapsed(uid: string): boolean {
  return collapsedEvaluations[uid];
}

function toggleCollapsed(uid: string): void {
  collapsedEvaluations[uid] = !isCollapsed(uid);
}

const activeStepUids = computed<string[]>(() =>
  activeStep.value === 'groups'
    ? evaluationGroups.value.map(group => group.uid)
    : displayedEvaluations.value.map(evaluation => evaluation.uid)
);

const allItemsCollapsed = computed<boolean>(
  () => activeStepUids.value.length > 0 && activeStepUids.value.every(isCollapsed)
);

function toggleAllItems(): void {
  // Expand all only when everything is collapsed; collapse all otherwise.
  const collapse = !allItemsCollapsed.value;
  for (const uid of activeStepUids.value) {
    collapsedEvaluations[uid] = collapse;
  }
}

/** The step header's primary action — one button per step, as in the mock. */
function handleAddItem(): void {
  switch (activeStep.value) {
    case 'extraction':
      openCreateExtractionEvaluation();
      return;
    case 'evaluations':
      openCreateClassificationEvaluation();
      return;
    case 'uncommons':
      openCreateUncommonsEvaluation();
      return;
    case 'clauses':
      openAddClauseEvaluation();
      return;
    case 'groups':
      openCreateGroupModal();
      return;
    default:
      return;
  }
}

function openEvaluationByUid(uid: string): void {
  const evaluation = evaluationsByUid.value.get(uid);
  if (evaluation) openEditEvaluation(evaluation);
}

function removeGroupMember(group: PlaybookEvaluationGroupInDb, uid: string): void {
  const evaluation = evaluationsByUid.value.get(uid);
  if (evaluation) void removeEvaluationFromGroup(group, evaluation);
}

function groupActionItems(group: PlaybookEvaluationGroupInDb): GaiaRemovableItem[] {
  return groupActionEntries(group).map(entry => ({
    key: entry.action.uid,
    label: entry.action.name,
    icon: actionTypeIcon(entry.action.action_type),
  }));
}

function editGroupAction(group: PlaybookEvaluationGroupInDb, uid: string): void {
  const entry = groupActionEntries(group).find(candidate => candidate.action.uid === uid);
  if (entry) openEditAction(entry.triggerUid, entry.action, 'group');
}

function removeGroupAction(group: PlaybookEvaluationGroupInDb, uid: string): void {
  const entry = groupActionEntries(group).find(candidate => candidate.action.uid === uid);
  if (entry) confirmRemove(`Delete “${entry.action.name}”?`, () => void deleteAction(entry.action));
}

function editOptionAction(option: PlaybookEvaluationOption | null, uid: string): void {
  const action = findAction(optionActions(option), uid);
  if (action) openEditAction(option?.trigger_uid, action);
}

function removeOptionAction(option: PlaybookEvaluationOption | null, uid: string): void {
  const action = findAction(optionActions(option), uid);
  if (action) confirmRemove(`Delete “${action.name}”?`, () => void deleteAction(action));
}

function editTriggerAction(trigger: PlaybookFindingThresholdTrigger, uid: string): void {
  const action = findAction(triggerActions(trigger), uid);
  if (action) openEditAction(trigger.uid, action);
}

function removeTriggerAction(trigger: PlaybookFindingThresholdTrigger, uid: string): void {
  const action = findAction(triggerActions(trigger), uid);
  if (action) confirmRemove(`Delete “${action.name}”?`, () => void deleteAction(action));
}

/**
 * The wizard footer carries one primary action. On the last step of an edit it
 * saves and returns to the list; everywhere else it is the create/continue path,
 * which persists the setup form on its way through.
 */
function handleFooterNext(): void {
  if (isEdit.value && !nextStep.value) {
    void handleSaveChanges();
    return;
  }
  void handleContinue();
}

onBeforeUnmount(() => {
  unsubscribeFromPlaybookUpdates();
});

onMounted(() => {
  void contractTypeStore.ensureLoaded();
  const uid = routedPlaybookUid.value;
  if (uid) {
    void loadPlaybookFromRoute(uid);
  }
});

watch(routedPlaybookUid, uid => {
  if (uid) {
    void loadPlaybookFromRoute(uid);
    return;
  }

  unsubscribeFromPlaybookUpdates();
  createdPlaybook.value = null;
  resetEvaluationState();
});
</script>

<template>
  <wizard-layout>
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.review }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'playbooks' }">{{ navLabels.playbooks }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ isEdit ? 'Edit playbook' : 'Create playbook' }}</n-breadcrumb-item>
    </template>
    <template #rail>
      <n-flex vertical :size="14" :wrap="false" class="gaia-wizard-rail">
        <n-flex vertical :size="4" :wrap="false">
          <gaia-header :level="1" size="page">
            {{ isEdit ? 'Edit playbook' : 'Create playbook' }}
          </gaia-header>
          <gaia-text
            class="gaia-wizard-rail__hint"
            size="meta"
            tone="subtle"
            :style="{ lineHeight: 1.5 }"
          >
            Start in Setup. Once the playbook is named you can edit any section, in any order.
          </gaia-text>
        </n-flex>
        <gaia-step-flow
          data-tour="pb.steps"
          :items="railItems"
          :active="activeStep"
          @select="key => moveToStep(key as StepKey)"
        />
      </n-flex>
    </template>

    <n-flex vertical :size="28" :wrap="false">
      <n-flex vertical :size="6" :wrap="false">
        <gaia-header :level="2">{{ activeStepDefinition.heading }}</gaia-header>
        <gaia-text size="body" tone="tertiary" :style="{ lineHeight: 1.55, textWrap: 'pretty' }">
          {{ activeStepDefinition.subtitle }}
        </gaia-text>
      </n-flex>

      <gaia-banner v-if="error" closable @close="error = null">{{ error }}</gaia-banner>

      <!-- ─────────────────────────────  Setup  ───────────────────────────── -->
      <template v-if="activeStep === 'setup'">
        <n-flex vertical :size="14" :wrap="false" data-tour="pb.setup-details">
          <gaia-section-header variant="rule">Playbook details</gaia-section-header>

          <gaia-form-field
            label="Playbook name"
            required
            :error="setupNameError ? 'Give the playbook a name before continuing' : undefined"
          >
            <gaia-input
              v-model:value="form.name"
              placeholder="e.g. NDA — inbound (DE)"
              :disabled="fieldsDisabled"
              @blur="nameTouched = true"
            />
          </gaia-form-field>

          <n-grid cols="1 s:2" responsive="screen" :x-gap="14" :y-gap="12">
            <n-grid-item>
              <gaia-form-field label="Contract types" hint="Leave empty to apply to any type">
                <gaia-select
                  v-model:value="form.contract_type_uids"
                  :options="contractTypeStore.uidOptions"
                  multiple
                  filterable
                  max-tag-count="responsive"
                  :disabled="fieldsDisabled"
                />
              </gaia-form-field>
            </n-grid-item>
            <n-grid-item>
              <gaia-form-field label="Party position" required>
                <gaia-select
                  v-model:value="form.party_position"
                  :options="partyPositionOptions"
                  :disabled="fieldsDisabled"
                />
              </gaia-form-field>
            </n-grid-item>
            <n-grid-item>
              <gaia-form-field label="Language" required>
                <gaia-input
                  v-model:value="form.language"
                  placeholder="English"
                  :disabled="fieldsDisabled"
                />
              </gaia-form-field>
            </n-grid-item>
            <n-grid-item>
              <gaia-form-field label="Status">
                <gaia-segmented
                  block
                  size="medium"
                  :value="form.status ?? PlaybookStatus.DRAFT"
                  :options="statusSegmentOptions"
                  @update:value="value => (form.status = value as PlaybookStatus)"
                />
              </gaia-form-field>
            </n-grid-item>
          </n-grid>

          <div data-tour="pb.setup-prompts">
            <gaia-form-field label="Description" hint="Optional — shown in the playbook list">
              <gaia-textarea
                v-model:value="form.description"
                placeholder="What this playbook checks, and when to use it."
                :autosize="{ minRows: 3, maxRows: 6 }"
                :disabled="fieldsDisabled"
              />
            </gaia-form-field>
          </div>

          <gaia-link :underline="false" @click="showSummaryPrompt = !showSummaryPrompt">
            {{ showSummaryPrompt ? 'Hide summary prompt' : 'Add a summary prompt' }}
          </gaia-link>

          <gaia-form-field
            v-if="showSummaryPrompt"
            label="Summary prompt"
            hint="Guides how each run summary is written"
          >
            <gaia-textarea
              v-model:value="form.summary_prompt"
              placeholder="e.g. Lead with deal-breakers, then note deviations from our standard."
              :autosize="{ minRows: 3, maxRows: 6 }"
              :disabled="fieldsDisabled"
            />
          </gaia-form-field>
        </n-flex>

        <n-flex
          v-if="showCreationPanel"
          vertical
          :size="14"
          :wrap="false"
          data-tour="pb.setup-reference"
        >
          <gaia-section-header variant="rule">How the content is created</gaia-section-header>

          <n-flex :size="10" role="radiogroup" aria-label="Creation method">
            <gaia-choice-card
              v-for="method in creationMethods"
              :key="method.value"
              :label="method.title"
              :meta="method.description"
              :selected="creationMethod === method.value"
              @select="creationMethod = method.value"
            />
          </n-flex>

          <!-- Generate with AI: reference contract, instructions, options. -->
          <gaia-sub-panel v-if="isAiCreation">
            <n-flex vertical :size="18" :wrap="false">
              <n-flex vertical :size="9" :wrap="false">
                <n-flex align="center" :size="10">
                  <gaia-text size="dense" weight="bold" tone="ink">Reference contract</gaia-text>
                  <gaia-text size="meta" tone="subtle">
                    Optional if you give generation instructions
                  </gaia-text>
                  <gaia-segmented
                    :value="sourceMode"
                    :options="referenceSourceOptions"
                    :style="{ marginLeft: 'auto', flex: 'none' }"
                    @update:value="value => (sourceMode = value as SourceMode)"
                  />
                </n-flex>

                <!-- Uploaded on Create, into the folder picked here (My Vault
                     unless changed). -->
                <gaia-vault-upload-panel
                  v-if="sourceMode === 'upload'"
                  ref="referenceUploadPanel"
                  selectable-destination
                  accept=".doc,.docx,.pdf"
                  :max="1"
                  :unpack-archives="false"
                  hint="One reference contract — PDF, DOC or DOCX"
                  :disabled="submitting || Boolean(createdPlaybook)"
                  @update:file-count="referenceUploadCount = $event"
                />

                <template v-else>
                  <!-- The reference is usually a contract's document: contracts open
                       like folders onto their files. -->
                  <vault-file-browser
                    v-model:selected-keys="selectedVaultKeys"
                    :single-selection="true"
                    :file-only-selection="true"
                    contracts="open"
                    :allowed-file-types="['pdf', 'doc', 'docx']"
                    file-type-error-msg="Select a PDF, DOC, or DOCX contract file."
                    :disabled="submitting || Boolean(createdPlaybook)"
                  />
                  <gaia-text size="meta" tone="subtle">{{ selectedVaultFileLabel }}</gaia-text>
                </template>
              </n-flex>

              <n-flex vertical :size="7" :wrap="false">
                <gaia-form-field label="Generation instructions">
                  <gaia-textarea
                    v-model:value="generationPrompt"
                    placeholder="Describe what this playbook should cover, the positions it should take, and any hard requirements."
                    :autosize="{ minRows: 4, maxRows: 10 }"
                    :disabled="fieldsDisabled"
                  />
                </gaia-form-field>
                <gaia-text size="meta" tone="subtle">
                  Provide a reference contract, instructions, or both.
                </gaia-text>
              </n-flex>

              <n-flex vertical :size="8" :wrap="false">
                <gaia-text size="dense" weight="bold" tone="ink">Generation options</gaia-text>
                <gaia-toggle-row
                  card
                  label="Create follow-up actions"
                  hint="Generate follow-up actions for this playbook's evaluations. Turn it off to add them manually later."
                  :checked="Boolean(form.add_actions)"
                  :disabled="fieldsDisabled"
                  @update:checked="value => (form.add_actions = value)"
                />
                <gaia-toggle-row
                  card
                  label="Save reusable clauses"
                  hint="Save useful new clauses from the reference contract to your organisation's clause library. Duplicates are skipped."
                  :checked="Boolean(form.add_clauses_to_library)"
                  :disabled="fieldsDisabled"
                  @update:checked="value => (form.add_clauses_to_library = value)"
                />
              </n-flex>
            </n-flex>
          </gaia-sub-panel>

          <!-- Use a template: the curated playbooks, grouped. -->
          <template v-else-if="isTemplateCreation">
            <gaia-search-input
              v-model:value="templateSearch"
              variant="panel"
              size="medium"
              placeholder="Search templates"
              :disabled="submitting"
            />
            <gaia-banner v-if="templatesError">{{ templatesError }}</gaia-banner>
            <n-spin :show="templatesLoading">
              <n-flex v-if="templateGroups.length" vertical :size="12" :wrap="false">
                <n-flex
                  v-for="group in templateGroups"
                  :key="group.label"
                  vertical
                  :size="4"
                  :wrap="false"
                >
                  <gaia-section-header>{{ group.label }}</gaia-section-header>
                  <gaia-picker-option
                    v-for="template in group.templates"
                    :key="template.key"
                    radio
                    :label="template.name"
                    :meta="templateMetaLabel(template)"
                    :selected="selectedTemplateKey === template.key"
                    :disabled="submitting"
                    @select="selectedTemplateKey = template.key"
                  />
                </n-flex>
              </n-flex>
              <gaia-empty-state
                v-else-if="!templatesLoading && !templatesError"
                dashed
                :title="
                  templateSearch.trim()
                    ? 'No templates match your search'
                    : 'No playbook templates are available'
                "
              />
            </n-spin>
          </template>

          <!-- Start from scratch: nothing is generated. -->
          <gaia-banner v-else type="info">
            You'll add extractions, evaluations and clauses yourself in the next steps. Nothing is
            generated — the playbook starts empty.
          </gaia-banner>
        </n-flex>
      </template>

      <!-- ──────────────────────────  Item steps  ────────────────────────── -->
      <template v-else>
        <gaia-banner v-if="evaluationsError">{{ evaluationsError }}</gaia-banner>

        <gaia-banner v-if="waitingForGeneratedEvaluations" type="info">
          Generating playbook… this runs in the background and may take a moment. Content appears
          here automatically when processing finishes.
        </gaia-banner>

        <gaia-banner
          v-if="activeStep === 'evaluations' && classificationEvaluationsMissingOptions.length > 0"
          type="warning"
        >
          Each classification evaluation needs at least one option before you can continue:
          {{ classificationEvaluationsMissingOptions.map(evaluationDisplayName).join(', ') }}. Edit
          the evaluation to add options, or delete it.
        </gaia-banner>

        <n-spin :show="evaluationsLoading">
          <n-flex vertical :size="14" :wrap="false" data-tour="pb.section-list">
            <n-flex align="center" :size="12">
              <gaia-section-header>{{ itemsCountLabel }}</gaia-section-header>
              <gaia-tag v-if="showGeneratedBadge" variant="accent" size="tiny">Generated</gaia-tag>
              <n-flex
                align="center"
                :size="8"
                :wrap="false"
                :style="{ marginLeft: 'auto', flex: 'none' }"
              >
                <!-- Slotted label: it adds clauses like the button beside it,
                     but from the library rather than blank. -->
                <gaia-add-button
                  v-if="activeStep === 'clauses'"
                  :icon="false"
                  @click="openImportClauseEvaluation"
                >
                  Import from library
                </gaia-add-button>
                <gaia-button v-if="activeStepUids.length" size="small" @click="toggleAllItems">
                  {{ allItemsCollapsed ? 'Expand all' : 'Collapse all' }}
                </gaia-button>
                <gaia-add-button variant="primary" @click="handleAddItem">
                  {{ activeStepDefinition.addLabel }}
                </gaia-add-button>
              </n-flex>
            </n-flex>

            <!-- Groups: trigger, assigned evaluations, actions. -->
            <template v-if="activeStep === 'groups'">
              <n-flex v-if="evaluationGroups.length" vertical :size="10" :wrap="false">
                <gaia-playbook-item
                  v-for="group in evaluationGroups"
                  :key="group.uid"
                  :title="groupDisplayName(group)"
                  :count-label="`${evaluationsForGroup(group).length} evaluations`"
                  :expanded="!isCollapsed(group.uid)"
                  add-label="Add trigger"
                  @toggle="toggleCollapsed(group.uid)"
                  @add="openGroupTriggerModal(group)"
                  @edit="openEditGroupModal(group)"
                  @remove="
                    confirmRemove(`Delete “${groupDisplayName(group)}”?`, () => deleteGroup(group))
                  "
                >
                  <n-flex
                    vertical
                    :size="10"
                    :wrap="false"
                    :style="{ flex: '1 1 100%', minWidth: 0 }"
                  >
                    <gaia-trigger-row v-if="groupTriggerSummaries(group).length">
                      <gaia-text size="dense" tone="ink">
                        {{ groupTriggerSummaries(group).join('; ') }}
                      </gaia-text>
                    </gaia-trigger-row>

                    <gaia-removable-list
                      editable
                      :items="groupMemberItems(group)"
                      empty-text="No evaluations assigned to this group."
                      @edit="uid => openEvaluationByUid(uid)"
                      @remove="uid => removeGroupMember(group, uid)"
                    />

                    <gaia-select
                      size="small"
                      filterable
                      :value="null"
                      :options="buildAddEvaluationOptions(group)"
                      :consistent-menu-width="false"
                      placeholder="Add an evaluation"
                      @update:value="
                        value => handleAddEvaluationToGroupSelect(value as string | null, group)
                      "
                    />

                    <gaia-removable-list
                      intent="delete"
                      label="Actions"
                      add-label="Add action"
                      editable
                      :items="groupActionItems(group)"
                      :empty-text="
                        groupTriggerForActions(group)
                          ? 'No actions defined.'
                          : 'Add a trigger to add actions.'
                      "
                      @add="openCreateAction(groupTriggerForActions(group), { scope: 'group' })"
                      @edit="uid => editGroupAction(group, uid)"
                      @remove="uid => removeGroupAction(group, uid)"
                    />
                  </n-flex>
                </gaia-playbook-item>
              </n-flex>

              <gaia-empty-state
                v-else
                dashed
                :title="`No ${activeStepDefinition.title.toLowerCase()} yet`"
              >
                {{ activeStepDefinition.subtitle }}
                <template #actions>
                  <gaia-button type="primary" size="small" @click="handleAddItem">
                    {{ activeStepDefinition.addLabel }}
                  </gaia-button>
                </template>
              </gaia-empty-state>
            </template>

            <!-- Extractions, Evaluations, Uncommons, Clauses. -->
            <template v-else>
              <n-flex v-if="hasDisplayedEvaluations" vertical :size="10" :wrap="false">
                <gaia-playbook-item
                  v-for="evaluation in displayedEvaluations"
                  :key="evaluation.uid"
                  :title="evaluationDisplayName(evaluation)"
                  :type-label="itemTypeLabel(evaluation)"
                  :expanded="!isCollapsed(evaluation.uid)"
                  @toggle="toggleCollapsed(evaluation.uid)"
                  @edit="openEditEvaluation(evaluation)"
                  @remove="
                    confirmRemove(`Delete “${evaluationDisplayName(evaluation)}”?`, () =>
                      deleteEvaluation(evaluation)
                    )
                  "
                >
                  <!-- An evaluation's groups belong to the row itself, so they
                       ride in the header beside the type chip rather than in a
                       panel below the body. Extractions are not groupable. -->
                  <template v-if="!isExtractionEvaluation(evaluation)" #chips>
                    <gaia-tag
                      v-for="group in groupsForEvaluation(evaluation)"
                      :key="group.uid"
                      variant="accent"
                      size="medium"
                      closable
                      @close="removeEvaluationFromGroupUid(evaluation, group.uid)"
                    >
                      <template #icon>
                        <n-icon :component="IconAlbums" :size="11" />
                      </template>
                      {{ groupDisplayName(group) }}
                    </gaia-tag>
                  </template>

                  <!-- Assigning a group is a row action, so it sits with Edit
                       and Remove rather than among the group chips. -->
                  <template v-if="!isExtractionEvaluation(evaluation)" #action>
                    <n-popover trigger="click" placement="bottom-end" :show-arrow="false">
                      <template #trigger>
                        <gaia-icon-button
                          :dimension="26"
                          quaternary
                          title="Assign to group"
                          aria-label="Assign to group"
                        >
                          <n-icon :component="IconAdd" :size="14" />
                        </gaia-icon-button>
                      </template>
                      <gaia-select
                        size="small"
                        filterable
                        :value="null"
                        :options="buildGroupOptions(evaluation)"
                        :consistent-menu-width="false"
                        placeholder="Assign to group"
                        :style="{ minWidth: '240px' }"
                        @update:value="
                          value => handleGroupOptionSelect(value as string, evaluation)
                        "
                      />
                    </n-popover>
                  </template>

                  <!-- Clause: the clause text and its three positions. -->
                  <template v-if="isClauseValidationEvaluation(evaluation)">
                    <gaia-sub-panel tone="plain" label="Clause text" :style="{ flex: '1 1 260px' }">
                      <template #actions>
                        <gaia-button size="tiny" @click="openSourceClauseEdit(evaluation)">
                          Edit clause
                        </gaia-button>
                      </template>
                      <n-flex vertical :size="10" :wrap="false">
                        <gaia-text size="dense" :style="{ lineHeight: 1.65, textWrap: 'pretty' }">
                          {{ clauseValidationContent(evaluation) }}
                        </gaia-text>
                        <!-- A stacked two-column grid, not GaiaMetaStrip: the
                             strip is one horizontal run with dividers between
                             its cells, and in a column this narrow five cells
                             wrap onto three lines with the dividers landing
                             mid-row. -->
                        <template v-if="clauseMetadata(evaluation).length">
                          <gaia-divider variant="subtle" :style="{ margin: '2px 0 0' }" />
                          <n-grid :cols="2" :x-gap="14" :y-gap="10">
                            <n-grid-item
                              v-for="item in clauseMetadata(evaluation)"
                              :key="item.label"
                            >
                              <gaia-field-row dense variant="stacked" :label="item.label">
                                {{ item.value }}
                              </gaia-field-row>
                            </n-grid-item>
                          </n-grid>
                        </template>
                      </n-flex>
                    </gaia-sub-panel>

                    <gaia-sub-panel tone="plain" label="Positions" :style="{ flex: '1.2 1 300px' }">
                      <n-flex vertical :size="9" :wrap="false">
                        <gaia-outcome-card
                          v-for="optionType in playbookOptionTypes"
                          :key="optionType"
                          tone="tinted"
                        >
                          <template #chip>
                            <gaia-clause-tier-chip :tier="clauseTierMap[optionType].tier">
                              {{ clauseTierMap[optionType].label }}
                            </gaia-clause-tier-chip>
                          </template>
                          <n-flex vertical :size="8" :wrap="false">
                            <gaia-textarea
                              :value="optionTypeDraft(evaluation, optionType)"
                              :placeholder="optionTypePlaceholder(optionType)"
                              :autosize="{ minRows: 3, maxRows: 10 }"
                              @update:value="
                                value => setOptionTypeDraft(evaluation, optionType, value)
                              "
                            />
                            <gaia-button
                              size="tiny"
                              type="primary"
                              :loading="optionSaving[optionDraftKey(evaluation, optionType)]"
                              :style="{ alignSelf: 'flex-start' }"
                              @click="saveClauseValidationOption(evaluation, optionType)"
                            >
                              Save
                            </gaia-button>
                          </n-flex>
                          <template #footer>
                            <gaia-removable-list
                              intent="delete"
                              flush
                              label="Actions"
                              add-label="Add action"
                              editable
                              :items="
                                actionItems(optionActions(optionForType(evaluation, optionType)))
                              "
                              :empty-text="
                                optionForType(evaluation, optionType)
                                  ? 'No actions defined.'
                                  : 'Save this position to add actions.'
                              "
                              @add="
                                openCreateActionForOption(
                                  evaluation,
                                  optionForType(evaluation, optionType)
                                )
                              "
                              @edit="
                                uid => editOptionAction(optionForType(evaluation, optionType), uid)
                              "
                              @remove="
                                uid =>
                                  removeOptionAction(optionForType(evaluation, optionType), uid)
                              "
                            />
                          </template>
                        </gaia-outcome-card>
                      </n-flex>
                    </gaia-sub-panel>
                  </template>

                  <!-- Uncommons: the prompt and its finding triggers. -->
                  <template v-else-if="isUncommonsEvaluation(evaluation)">
                    <gaia-sub-panel tone="plain" label="Prompt" :style="{ flex: '1 1 260px' }">
                      {{ evaluation.extraction_prompt }}
                    </gaia-sub-panel>

                    <gaia-sub-panel
                      tone="plain"
                      label="Finding triggers"
                      :style="{ flex: '1.2 1 300px' }"
                    >
                      <template #actions>
                        <gaia-add-button
                          subject="finding trigger"
                          size="xs"
                          :icon="false"
                          @click="openCreateTrigger(evaluation)"
                        />
                      </template>
                      <n-flex vertical :size="12" :wrap="false">
                        <gaia-text
                          v-if="!evaluationTriggers(evaluation).length"
                          size="meta"
                          tone="faint"
                        >
                          No finding triggers have been defined.
                        </gaia-text>
                        <n-flex
                          v-for="trigger in evaluationTriggers(evaluation)"
                          :key="trigger.uid"
                          vertical
                          :size="8"
                          :wrap="false"
                        >
                          <gaia-trigger-row label="When">
                            <n-flex align="center" :size="8" :wrap="false">
                              <gaia-text
                                truncate
                                size="dense"
                                tone="ink"
                                :style="{ flex: 1, minWidth: 0 }"
                              >
                                {{ formatFindingThresholdRule(trigger) }}
                              </gaia-text>
                              <gaia-button
                                size="tiny"
                                @click="openEditTrigger(evaluation, trigger)"
                              >
                                Edit
                              </gaia-button>
                              <gaia-delete-button
                                size="xs"
                                subject="trigger"
                                @click="
                                  confirmRemove('Delete this finding trigger?', () =>
                                    deleteTrigger(trigger)
                                  )
                                "
                              />
                            </n-flex>
                          </gaia-trigger-row>
                          <gaia-removable-list
                            intent="delete"
                            flush
                            label="Actions"
                            add-label="Add action"
                            editable
                            :items="actionItems(triggerActions(trigger))"
                            empty-text="No actions defined."
                            @add="openCreateAction(trigger.uid)"
                            @edit="uid => editTriggerAction(trigger, uid)"
                            @remove="uid => removeTriggerAction(trigger, uid)"
                          />
                        </n-flex>
                      </n-flex>
                    </gaia-sub-panel>
                  </template>

                  <!-- Extraction: the prompt and the response type it answers in. -->
                  <template v-else-if="isExtractionEvaluation(evaluation)">
                    <gaia-sub-panel tone="plain" label="Prompt" :style="{ flex: '1 1 260px' }">
                      {{ evaluation.extraction_prompt }}
                    </gaia-sub-panel>
                    <gaia-sub-panel
                      tone="plain"
                      label="Response type"
                      :style="{ flex: '0 1 220px' }"
                    >
                      <gaia-text size="dense" tone="ink">
                        {{ extractionTypeLabel(extractionResponseType(evaluation)) }}
                      </gaia-text>
                    </gaia-sub-panel>
                  </template>

                  <!-- Classification: the prompt, its outcomes, its groups. -->
                  <template v-else>
                    <gaia-sub-panel tone="plain" label="Prompt" :style="{ flex: '1 1 240px' }">
                      {{ evaluation.extraction_prompt }}
                    </gaia-sub-panel>

                    <gaia-sub-panel tone="plain" label="Outcomes" :style="{ flex: '1.35 1 300px' }">
                      <n-flex vertical :size="9" :wrap="false">
                        <gaia-text v-if="!evaluation.options?.length" size="meta" tone="faint">
                          No options have been defined.
                        </gaia-text>
                        <gaia-outcome-card
                          v-for="option in evaluation.options"
                          :key="option.uid"
                          tone="tinted"
                          :name="option.name"
                        >
                          <template v-if="option.risk_level" #chip>
                            <gaia-risk-pill :level="playbookRiskLevelMap[option.risk_level].level">
                              {{ playbookRiskLevelMap[option.risk_level].label }}
                            </gaia-risk-pill>
                          </template>
                          {{ option.definition }}
                          <gaia-text
                            v-if="optionClauseName(option)"
                            size="meta"
                            tone="subtle"
                            :style="{ display: 'block', marginTop: '6px' }"
                          >
                            Linked clause: {{ optionClauseName(option) }}
                          </gaia-text>
                          <template #footer>
                            <gaia-removable-list
                              intent="delete"
                              flush
                              label="Actions"
                              add-label="Add action"
                              editable
                              :items="actionItems(optionActions(option))"
                              empty-text="No actions defined."
                              @add="openCreateActionForOption(evaluation, option)"
                              @edit="uid => editOptionAction(option, uid)"
                              @remove="uid => removeOptionAction(option, uid)"
                            />
                          </template>
                        </gaia-outcome-card>
                      </n-flex>
                    </gaia-sub-panel>
                  </template>
                </gaia-playbook-item>
              </n-flex>

              <gaia-empty-state
                v-else
                dashed
                :title="`No ${activeStepDefinition.title.toLowerCase()} yet`"
              >
                {{ activeStepDefinition.subtitle }}
                <template #actions>
                  <gaia-add-button
                    v-if="activeStep === 'clauses'"
                    :icon="false"
                    @click="openImportClauseEvaluation"
                  >
                    Import from library
                  </gaia-add-button>
                  <gaia-add-button variant="primary" :icon="false" @click="handleAddItem">
                    {{ activeStepDefinition.addLabel }}
                  </gaia-add-button>
                </template>
              </gaia-empty-state>
            </template>
          </n-flex>
        </n-spin>
      </template>
    </n-flex>

    <template #footer>
      <gaia-wizard-footer
        :note="footerNote"
        quiet-label="Cancel"
        :back-label="canGoBack ? 'Back' : undefined"
        :next-label="continueLabel"
        :loading="submitting || loadingPlaybook"
        :next-disabled="continueDisabled"
        :next-attrs="{ 'data-tour': 'pb.continue' }"
        @quiet="navigateBack"
        @back="goBack"
        @next="handleFooterNext"
      />
    </template>
  </wizard-layout>

  <GaiaClauseFormModal
    v-model:show="showClauseEditModal"
    :mode="clauseEditMode"
    :clause="editingSourceClause"
    library="organisation"
    @saved="handleClauseModalSaved"
  />

  <ClausePickerModal
    v-model:show="showClausePickerModal"
    :imported-clause-uids="importedClauseUids"
    @selected="handleClausePicked"
  />

  <PlaybookActionModal
    v-model:show="showActionModal"
    v-model:saving="actionModalSaving"
    :mode="actionModalMode"
    :trigger-uid="actionModalTriggerUid"
    :evaluation-uid="actionModalEvaluationUid"
    :option-uid="actionModalOptionUid"
    :action="actionModalAction"
    :evaluations="evaluations"
    :reusable-actions="reusableActions"
    :trigger-scope="actionModalTriggerScope"
    @saved="handleActionSaved"
  />

  <UncommonsTriggerModal
    v-model:show="showTriggerModal"
    v-model:saving="triggerModalSaving"
    :mode="triggerModalMode"
    :evaluation-uid="triggerModalEvaluationUid"
    :trigger="triggerModalTrigger"
    @saved="handleTriggerSaved"
  />

  <n-modal
    v-model:show="showClassificationOptionsModal"
    preset="card"
    :title="
      classificationModalMode === 'create' ? 'Create classification' : 'Edit classification options'
    "
    style="width: min(920px, calc(100vw - 32px))"
    :mask-closable="false"
    @after-leave="closeClassificationOptionsModal"
  >
    <n-flex vertical :size="14">
      <n-form label-placement="top">
        <n-form-item label="Name" required>
          <gaia-input
            v-model:value="classificationOptionsModalName"
            placeholder="Classification name"
          />
        </n-form-item>

        <n-form-item label="Choice type">
          <!-- The model is a boolean; the track speaks in values, so the two
               are mapped here rather than storing a string. -->
          <gaia-segmented
            :value="classificationOptionsModalMultiple ? 'multiple' : 'single'"
            :options="CHOICE_TYPE_OPTIONS"
            :disabled="classificationModalMode === 'edit'"
            @update:value="value => (classificationOptionsModalMultiple = value === 'multiple')"
          />
        </n-form-item>
        <p v-if="classificationModalMode === 'edit'" class="classification-options-modal__hint">
          Choice type can only be set when creating a classification.
        </p>

        <n-form-item label="Prompt" required>
          <gaia-textarea
            v-model:value="classificationOptionsModalPrompt"
            :autosize="{ minRows: 3, maxRows: 8 }"
          />
        </n-form-item>

        <header class="classification-options-modal__header">
          <span class="classification-options-modal__options-title">Options</span>
          <n-button size="small" secondary @click="addClassificationOptionModalRow">
            Add option
          </n-button>
        </header>

        <div v-if="classificationOptionsModalRows.length" class="classification-options-editor">
          <div
            v-for="row in classificationOptionsModalRows"
            :key="row.key"
            class="classification-options-editor__row"
          >
            <n-flex :wrap="false" align="flex-end" :size="12">
              <n-form-item label="Name" required style="flex: 1">
                <gaia-input v-model:value="row.name" />
              </n-form-item>
              <n-form-item label="Risk" style="width: 150px">
                <gaia-enum-select
                  v-model:value="row.riskLevel"
                  clearable
                  :enum-source="PlaybookRiskLevel"
                  :labels="playbookRiskLevelMap"
                />
              </n-form-item>
              <n-form-item label="Color">
                <n-color-picker
                  v-model:value="row.color"
                  :show-alpha="false"
                  :modes="['hex']"
                  :swatches="optionColorSwatches"
                >
                  <template #trigger="{ value, onClick, ref: triggerRef }">
                    <div
                      :ref="triggerRef"
                      class="classification-options-editor__swatch"
                      :style="{ backgroundColor: value || '#dbeafe' }"
                      @click="onClick"
                    />
                  </template>
                </n-color-picker>
              </n-form-item>
              <n-form-item label=" ">
                <gaia-delete-button
                  intent="remove"
                  subject="option"
                  @click="removeClassificationOptionModalRow(row)"
                />
              </n-form-item>
            </n-flex>

            <n-form-item label="Definition" required>
              <gaia-textarea
                v-model:value="row.definition"
                :autosize="{ minRows: 3, maxRows: 8 }"
              />
            </n-form-item>
          </div>
        </div>

        <n-empty
          v-else
          class="classification-options-modal__empty"
          description="No options have been defined."
        />
      </n-form>

      <n-space justify="end">
        <n-button @click="closeClassificationOptionsModal">Cancel</n-button>
        <n-button
          type="primary"
          :disabled="classificationOptionsModalRows.length === 0"
          :loading="classificationOptionsModalSaving"
          @click="saveClassificationOptionsModal"
        >
          {{ classificationModalMode === 'create' ? 'Create' : 'Save' }}
        </n-button>
      </n-space>
    </n-flex>
  </n-modal>

  <n-modal
    v-model:show="showUncommonsModal"
    preset="card"
    :title="uncommonsModalMode === 'create' ? 'Create uncommons check' : 'Edit uncommons check'"
    style="width: min(720px, calc(100vw - 32px))"
    :mask-closable="false"
    @after-leave="closeUncommonsModal"
  >
    <n-flex vertical :size="14">
      <n-form label-placement="top">
        <n-form-item label="Name" required>
          <gaia-input v-model:value="uncommonsModalName" placeholder="Uncommons check name" />
        </n-form-item>

        <n-form-item label="Prompt" required>
          <gaia-textarea
            v-model:value="uncommonsModalPrompt"
            placeholder="Describe what counts as an unusual or out-of-place passage."
            :autosize="{ minRows: 3, maxRows: 8 }"
          />
        </n-form-item>
      </n-form>

      <n-space justify="end">
        <n-button @click="closeUncommonsModal">Cancel</n-button>
        <n-button type="primary" :loading="uncommonsModalSaving" @click="saveUncommonsModal">
          {{ uncommonsModalMode === 'create' ? 'Create' : 'Save' }}
        </n-button>
      </n-space>
    </n-flex>
  </n-modal>

  <n-modal
    v-model:show="showExtractionModal"
    preset="card"
    :title="extractionModalMode === 'create' ? 'Create extraction' : 'Edit extraction'"
    style="width: min(720px, calc(100vw - 32px))"
    :mask-closable="false"
    @after-leave="closeExtractionModal"
  >
    <n-flex vertical :size="14">
      <n-form label-placement="top">
        <n-form-item label="Name" required>
          <gaia-input v-model:value="extractionModalName" placeholder="Extraction name" />
        </n-form-item>

        <n-form-item label="Prompt" required>
          <gaia-textarea
            v-model:value="extractionModalPrompt"
            placeholder="Describe what data point to extract from the document."
            :autosize="{ minRows: 3, maxRows: 8 }"
          />
        </n-form-item>

        <n-form-item
          label="Response type"
          required
          :feedback="
            extractionModalMode === 'edit'
              ? `The response type can't be changed after creation.`
              : undefined
          "
        >
          <gaia-select
            v-model:value="extractionModalResponseType"
            :options="extractionTypeOptions"
            :disabled="extractionModalMode === 'edit'"
          />
        </n-form-item>
      </n-form>

      <n-space justify="end">
        <n-button @click="closeExtractionModal">Cancel</n-button>
        <n-button type="primary" :loading="extractionModalSaving" @click="saveExtractionModal">
          {{ extractionModalMode === 'create' ? 'Create' : 'Save' }}
        </n-button>
      </n-space>
    </n-flex>
  </n-modal>

  <n-modal
    v-model:show="showCreateGroupModal"
    preset="card"
    title="Create group"
    style="width: min(420px, calc(100vw - 32px))"
    :mask-closable="false"
    @after-enter="focusNewGroupNameInput"
    @after-leave="closeCreateGroupModal"
  >
    <n-flex vertical :size="14">
      <n-form label-placement="top">
        <n-form-item label="Group name" required>
          <gaia-input
            ref="newGroupNameInput"
            v-model:value="newGroupName"
            placeholder="Enter a name for the group"
            @keyup.enter="submitCreateGroup"
          />
        </n-form-item>
      </n-form>

      <n-space justify="end">
        <n-button @click="closeCreateGroupModal">Cancel</n-button>
        <n-button
          type="primary"
          :loading="creatingGroup"
          :disabled="!newGroupName.trim()"
          @click="submitCreateGroup"
        >
          Create
        </n-button>
      </n-space>
    </n-flex>
  </n-modal>

  <n-modal
    v-model:show="showEditGroupModal"
    preset="card"
    title="Edit group"
    style="width: min(420px, calc(100vw - 32px))"
    :mask-closable="false"
    @after-enter="focusEditGroupNameInput"
    @after-leave="closeEditGroupModal"
  >
    <n-flex vertical :size="14">
      <n-form label-placement="top">
        <n-form-item label="Group name" required>
          <gaia-input
            ref="editGroupNameInput"
            v-model:value="editGroupName"
            placeholder="Enter a name for the group"
            @keyup.enter="submitEditGroup"
          />
        </n-form-item>
      </n-form>

      <n-space justify="end">
        <n-button @click="closeEditGroupModal">Cancel</n-button>
        <n-button
          type="primary"
          :loading="savingEditGroup"
          :disabled="!editGroupName.trim()"
          @click="submitEditGroup"
        >
          Save
        </n-button>
      </n-space>
    </n-flex>
  </n-modal>

  <GroupTriggerModal
    v-model:show="showGroupTriggerModal"
    :group="groupTriggerTarget"
    @saved="handleGroupTriggerSaved"
  />
</template>
